import styled, { keyframes } from "styled-components";
import { AVAILABLE_MACROS, DEFAULT_FOOD_MAIN_MACROS } from "../../../../config/nutritionMacros";
import { useAuth } from "../../../../hooks/useAuth";
import { hasPremiumAccess } from "../../../../utils/subscription";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import openFoodFactsService from "../../../../services/openFoodFactsService";
import journalService from "../../../../services/journalService";
import { buildFavoriteFoodPayload, matchesFavoriteFood } from "../../../nutrition/favoriteFoodUtils";
import favoriteFoodsService from "../../../../services/favoriteFoodsService";
import friendsService from "../../../../services/friendsService";
import settingsJournalService from "../../../../services/settingsJournalService";
import { buildAdjustedNutritionForDisplay } from "../../../../utils/nutritionData";
import { SharedDatePicker } from "../../../nutrition/SharedDatePicker.styled";
import Alert from "../../../common/Alert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp, faCalendarDays, faCheck, faChevronDown, faClipboardList, faClock, faCopy, faFileExcel, faFilePdf, faFire, faGripVertical, faMagnifyingGlass, faPen, faPlus, faStar, faStickyNote, faTrash, faXmark } from "@fortawesome/free-solid-svg-icons";
import FoodMacros from "../../../nutrition/FoodMacros";
import LoadingSpinner from "../../../common/LoadingSpinner";
import FoodImageModal, { getFoodImageGallery } from "../../../common/FoodImageModal";
import SummaryDemo from "./SummaryDemo";
import ScrollReveal from "../../ScrollReveal";

// ----------------------------------------------------------------------
// Animations
// ----------------------------------------------------------------------
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// ----------------------------------------------------------------------
// Composants de mise en page pour la landing page
// ----------------------------------------------------------------------
const TwoColRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 3rem;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  /* Permet d'inverser l'ordre des colonnes */
  ${({ $reverse }) =>
    $reverse &&
    `
    & > :nth-child(1) { order: 2; }
    & > :nth-child(2) { order: 1; }
  `}

  /* Animation */
  animation: ${fadeInUp} 0.35s ease-out both;
`;

/* Sections où le texte est au-dessus et le contenu prend toute la largeur */
const StackedFeature = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    gap: 1rem;
  }

  /* Animation */
  animation: ${fadeInUp} 0.35s ease-out both;
`;

const FeatureText = styled.div`
  h3 {
    font-family: "Poppins", sans-serif;
    font-size: 1.35rem;
    font-weight: 700;
    color: #1a2e40;
    margin: 0 0 0.75rem 0;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    svg {
      color: #2c5282;
      flex-shrink: 0;
    }
  }
  p {
    font-family: "Roboto", sans-serif;
    font-size: 0.95rem;
    color: #4a5568;
    line-height: 1.6;
    margin: 0;
  }
`;

// ----------------------------------------------------------------------
// Code original du JournalDemo
// ----------------------------------------------------------------------
const SUMMARY_GOAL_CONFIG = Object.freeze({
  'energy-kcal': { goalKey: 'targetCalories', color: '#4CAF50' },
  'proteins': { goalKey: 'targetProtein', color: '#E53935' },
  'carbohydrates': { goalKey: 'targetCarbs', color: '#FFEB3B' },
  'fat': { goalKey: 'targetFats', color: '#1A2E40' },
  'fiber': { goalKey: 'targetFiber', color: '#8BC34A' },
  'sugars': { goalKey: 'targetSugars', color: '#FF5722' },
  'added-sugars': { goalKey: 'targetAddedSugars', color: '#FF5722' },
  'starch': { goalKey: 'targetStarch', color: '#FFC107' },
  'saturated-fat': { goalKey: 'targetSaturatedFat', color: '#FF5722' },
  'trans-fat': { goalKey: 'targetTransFat', color: '#D32F2F' },
  'monounsaturated-fat': { goalKey: 'targetMonounsaturatedFat', color: '#4CAF50' },
  'polyunsaturated-fat': { goalKey: 'targetPolyunsaturatedFat', color: '#4CAF50' },
  'cholesterol': { goalKey: 'targetCholesterol', color: '#E91E63' },
  'sodium': { goalKey: 'targetSodium', color: '#FF9800' },
  'salt': { goalKey: 'targetSalt', color: '#FF9800' },
  'vitamin-a': { goalKey: 'targetVitaminA', color: '#FF9800' },
  'vitamin-b1': { goalKey: 'targetVitaminB1', color: '#CDDC39' },
  'vitamin-b2': { goalKey: 'targetVitaminB2', color: '#CDDC39' },
  'vitamin-b6': { goalKey: 'targetVitaminB6', color: '#CDDC39' },
  'vitamin-b9': { goalKey: 'targetVitaminB9', color: '#CDDC39' },
  'vitamin-b12': { goalKey: 'targetVitaminB12', color: '#CDDC39' },
  'vitamin-c': { goalKey: 'targetVitaminC', color: '#FFEB3B' },
  'vitamin-d': { goalKey: 'targetVitaminD', color: '#FF9800' },
  'vitamin-e': { goalKey: 'targetVitaminE', color: '#4CAF50' },
  'vitamin-k': { goalKey: 'targetVitaminK', color: '#4CAF50' },
  'calcium': { goalKey: 'targetCalcium', color: '#0097A7' },
  'iron': { goalKey: 'targetIron', color: '#0288D1' },
  'magnesium': { goalKey: 'targetMagnesium', color: '#9C27B0' },
  'zinc': { goalKey: 'targetZinc', color: '#607D8B' },
  'copper': { goalKey: 'targetCopper', color: '#795548' },
  'manganese': { goalKey: 'targetManganese', color: '#9E9E9E' },
  'phosphorus': { goalKey: 'targetPhosphorus', color: '#FF5722' },
  'potassium': { goalKey: 'targetPotassium', color: '#3F51B5' },
  'selenium': { goalKey: 'targetSelenium', color: '#607D8B' },
  'choline': { goalKey: 'targetCholine', color: '#9C27B0' },
  'caffeine': { goalKey: 'targetCaffeine', color: '#795548' },
  'alcohol': { goalKey: 'targetAlcohol', color: '#E91E63' },
});

const SUMMARY_STANDARD_MACROS = Object.freeze(['proteins', 'carbohydrates', 'fat']);
const ADVANCED_GOAL_SUMMARY_MACROS = new Set(['fiber', 'sugars', 'sodium', 'saturated-fat']);
const SUMMARY_FOCUSED_MACROS = new Set([
  ...SUMMARY_STANDARD_MACROS,
  'fiber',
  'sugars',
  'sodium',
  'saturated-fat',
]);

const SUMMARY_MACRO_MAPPING = Object.freeze(
  AVAILABLE_MACROS.reduce((mapping, macro) => {
    const goalConfig = SUMMARY_GOAL_CONFIG[macro.key] || {};
    mapping[macro.key] = {
      dataKey: macro.key,
      label: macro.label,
      unit: macro.unit,
      goalKey: goalConfig.goalKey || null,
      color: goalConfig.color || '#64748b'
    };
    return mapping;
  }, {})
);

const unwrapOpenFoodFactsProduct = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  if (value.product && typeof value.product === 'object' && !Array.isArray(value.product)) {
    return value.product;
  }

  if (
    value.data?.product
    && typeof value.data.product === 'object'
    && !Array.isArray(value.data.product)
  ) {
    return value.data.product;
  }

  return value;
};

const parseFoodSourceData = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    return unwrapOpenFoodFactsProduct(value);
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return unwrapOpenFoodFactsProduct(parsed);
    } catch {
      return null;
    }
  }

  return null;
};

const getFoodBarcode = (food, sourceData = null) => String(
  food?.barcode
  || food?.code
  || sourceData?.code
  || sourceData?._id
  || sourceData?.id
  || ''
).trim();

const FOOD_IMAGE_FIELDS = [
  'image_front_url',
  'image_front_small_url',
  'image_front_thumb_url',
  'image_ingredients_url',
  'image_ingredients_small_url',
  'image_nutrition_url',
  'image_nutrition_small_url',
  'image_packaging_url',
  'image_packaging_small_url',
  'image_url',
  'image_thumb_url',
  'image_small_url',
];

const buildFoodImagePreview = ({ food, sources, imageUrl, displayName, barcode }) => {
  const imageSources = sources.filter(Boolean);
  const preview = {
    src: String(imageUrl || '').trim(),
    name: displayName || 'Aliment',
    brand: '',
    code: barcode,
  };

  FOOD_IMAGE_FIELDS.forEach((field) => {
    preview[field] = imageSources
      .map((source) => String(source?.[field] || '').trim())
      .find(Boolean) || '';
  });

  preview.brand = imageSources
    .map((source) => String(source?.brands || source?.brand || '').trim())
    .find(Boolean)
    || String(food?.brand || food?.brands || '').trim();

  return preview;
};

const isCoarsePointerViewport = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (typeof window.matchMedia === 'function') {
    return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  }

  return false;
};

const FoodNote = styled.div`
  background: #fafafa;
  border-radius: clamp(8px, 2vw, 10px);
  padding: clamp(0.7rem, 2.5vw, 1.1rem) clamp(0.85rem, 2.8vw, 1.1rem);
  font-size: clamp(0.85rem, 2.2vw, 0.95rem);
  color: #333;
  line-height: 1.55;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08);
  margin: clamp(0.5rem, 1.5vw, 0.75rem) 0 clamp(0.75rem, 2vw, 1rem) 0;
  white-space: pre-wrap;
  word-wrap: break-word;

  @media (max-width: 576px) {
    border-radius: 8px;
    padding: 0.7rem 0.85rem;
    margin: 0.5rem 0 0.75rem 0;
  }

  @media (max-width: 400px) {
    padding: 0.6rem 0.75rem;
    margin: 0.4rem 0 0.6rem 0;
  }
`;

const NoteIcon = styled.div`
  display: inline-flex;
  align-items: center;
  gap: clamp(0.35rem, 1.5vw, 0.5rem);
  color: #777;
  font-weight: 600;
  margin-bottom: clamp(0.3rem, 1vw, 0.4rem);
  font-size: clamp(0.85rem, 2.3vw, 0.95rem);
  
  svg {
    width: clamp(13px, 3.5vw, 15px);
    height: clamp(13px, 3.5vw, 15px);
    flex-shrink: 0;
  }

  @media (max-width: 576px) {
    gap: 0.35rem;
    margin-bottom: 0.3rem;
    font-size: 0.85rem;

    svg {
      width: 13px;
      height: 13px;
    }
  }

  @media (max-width: 400px) {
    gap: 0.3rem;
    margin-bottom: 0.25rem;
    font-size: 0.8rem;

    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

const NoteText = styled.div`
  font-style: normal;
  white-space: pre-wrap;
  word-wrap: break-word;
  color: #444;
  font-size: clamp(0.8rem, 2.1vw, 0.9rem);
  line-height: 1.6;
  overflow-wrap: break-word;

  @media (max-width: 576px) {
    font-size: 0.8rem;
    line-height: 1.5;
  }

  @media (max-width: 400px) {
    font-size: 0.75rem;
    line-height: 1.45;
  }
`;

const JournalDemo = ({ friendUsername = null, demoData = null } = {}) => {
  const { user } = useAuth();
  const isPremium = hasPremiumAccess(user);
  const isFriendView = typeof friendUsername === 'string' && friendUsername.trim().length > 0;
  const isReadOnly = isFriendView;
  const mealTableColSpan = isReadOnly ? 7 : 8;
  const [journalId, setJournalId] = useState(null);
  const [allMeals, setAllMeals] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [filteredMeals, setFilteredMeals] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [draggedFood, setDraggedFood] = useState(null);
  const [foodOverTarget, setFoodOverTarget] = useState(null);
  const [selectedFoodsByMeal, setSelectedFoodsByMeal] = useState({});
  const [bulkMoveTargetByMeal, setBulkMoveTargetByMeal] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState(null);
  const [showToastMsg, setShowToastMsg] = useState(null);
  const [favoriteFoods, setFavoriteFoods] = useState([]);
  const [summaryMacros, setSummaryMacros] = useState([]);
  const [hideUnavailableFoodMacros, setHideUnavailableFoodMacros] = useState(false);
  const [displayedMacros, setDisplayedMacros] = useState([]);
  const [mainMacros, setMainMacros] = useState([]);
  const [nutritionalGoals, setNutritionalGoals] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [editingMealId, setEditingMealId] = useState(null);
  const [mealNameDrafts, setMealNameDrafts] = useState({});
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.innerWidth <= 1024;
  });
  const [isCoarsePointerMode, setIsCoarsePointerMode] = useState(() => isCoarsePointerViewport());
  const [foodImageCache, setFoodImageCache] = useState({});
  const [foodImagePreview, setFoodImagePreview] = useState(null);
  const PRODUCT_PLACEHOLDER_IMAGE = 'data:image/svg+xml;utf8,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2280%22 height=%2280%22 viewBox=%220 0 80 80%22%3E%3Crect width=%2280%22 height=%2280%22 fill=%22%23eef2f7%22/%3E%3Ctext x=%2240%22 y=%2245%22 text-anchor=%22middle%22 font-family=%22Arial,sans-serif%22 font-size=%2210%22 fill=%22%2362748a%22%3EProduit%3C/text%3E%3C/svg%3E';
  const [collapsedFoodIngredients, setCollapsedFoodIngredients] = useState({});
  const [lastActiveMealIndex, setLastActiveMealIndex] = useState(0);
  const [openMealActionsId, setOpenMealActionsId] = useState(null);
  const [isMobileHeaderActionsOpen, setIsMobileHeaderActionsOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const foodPreviewMacros = DEFAULT_FOOD_MAIN_MACROS;
  const allDetailMacroKeys = useMemo(
    () => AVAILABLE_MACROS.map((macro) => macro.key),
    []
  );

  const mealNameInputRefs = useRef({});
  const isSyncingQueuedMealsRef = useRef(false);
  const foodImageFetchRef = useRef(new Map());
  const foodImageSearchRef = useRef(new Map());
  const touchDragPreviewRef = useRef(null);
  const touchDragGhostFrameRef = useRef(null);
  const touchDragStateRef = useRef({
    active: false,
    type: null,
    pointerId: null,
    sourceMealIndex: null,
    targetMealIndex: null,
    sourceMealId: null,
    sourceFoodIndex: null,
    targetFoodIndex: null,
  });

  const openFoodImagePreview = useCallback(({
    food,
    sourceData,
    cachedProduct,
    cachedNameProduct,
    imageUrl,
    displayName,
    barcode,
  }) => {
    const normalizedBarcode = String(
      barcode
      || getFoodBarcode(food, sourceData)
      || cachedProduct?.code
      || cachedProduct?._id
      || cachedNameProduct?.code
      || cachedNameProduct?._id
    ).trim();
    const normalizedName = String(displayName || food?.name || '').trim().toLowerCase();
    const nameCacheKey = normalizedName ? `name:${normalizedName}` : '';
    const buildPreview = (fullProduct = null) => buildFoodImagePreview({
      food,
      sources: [fullProduct, cachedProduct, cachedNameProduct, sourceData, food],
      imageUrl,
      displayName,
      barcode: normalizedBarcode,
    });

    setFoodImagePreview(buildPreview());

    if (cachedProduct) {
      return;
    }

    const applyProductToPreview = (product) => {
      if (!product) {
        return;
      }

      setFoodImageCache((previous) => ({
        ...previous,
        ...(normalizedBarcode ? { [normalizedBarcode]: product } : {}),
        ...(nameCacheKey ? { [nameCacheKey]: product } : {}),
      }));
      setFoodImagePreview((current) => (
        current?.code === normalizedBarcode && current?.name === (displayName || 'Aliment')
          ? buildPreview(product)
          : current
      ));
    };

    if (!normalizedBarcode) {
      if (!normalizedName) {
        return;
      }

      openFoodFactsService
        .searchFoods({
          query: normalizedName,
          page: 1,
          pageSize: 1,
          filters: { sortBy: 'unique_scans_n' },
          allowFuzzy: false,
        })
        .then((data) => applyProductToPreview((data?.products || [])[0] || null))
        .catch(() => null);
      return;
    }

    const existingRequest = foodImageFetchRef.current.get(normalizedBarcode);
    const request = existingRequest || openFoodFactsService.getProductByCode({ code: normalizedBarcode });

    if (!existingRequest) {
      foodImageFetchRef.current.set(normalizedBarcode, request);
    }

    request
      .then((product) => {
        applyProductToPreview(product);
      })
      .catch(() => null)
      .finally(() => {
        if (!existingRequest) {
          foodImageFetchRef.current.delete(normalizedBarcode);
        }
      });
  }, []);

  const removeTouchDragPreview = useCallback(() => {
    if (touchDragPreviewRef.current?.parentNode) {
      touchDragPreviewRef.current.parentNode.removeChild(touchDragPreviewRef.current);
    }
    touchDragPreviewRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      if (touchDragGhostFrameRef.current) {
        window.cancelAnimationFrame(touchDragGhostFrameRef.current);
        touchDragGhostFrameRef.current = null;
      }
      removeTouchDragPreview();
      if (typeof document !== 'undefined') {
        document.body.classList.remove('touch-drag-active');
      }
    };
  }, [removeTouchDragPreview]);

  const processedImageKeysRef = useRef(new Set());

  useEffect(() => {
    if (!filteredMeals || filteredMeals.length === 0) {
      return;
    }

    if (demoData) {
      const allHaveImages = filteredMeals.every((meal) => {
        return (meal.foods || []).every((food) => {
          const source = parseFoodSourceData(
            food?.sourceData ||
            food?.source_data ||
            food?.openFoodFactsData ||
            null
          );
          return Boolean(
            source?.image_front_small_url ||
            source?.image_thumb_url ||
            source?.image_front_url ||
            source?.image_url ||
            food?.imageUrl ||
            food?.image_url ||
            food?.image
          );
        });
      });

      if (allHaveImages) {
        return;
      }
    }

    const codesToFetch = new Set();
    const namesToFetch = new Set();

    filteredMeals.forEach((meal) => {
      (meal.foods || []).forEach((food) => {
        const source = parseFoodSourceData(
          food?.sourceData ||
          food?.source_data ||
          food?.openFoodFactsData ||
          null
        );
        const barcode = getFoodBarcode(food, source);

        const hasImage = Boolean(
          source?.image_front_small_url ||
          source?.image_thumb_url ||
          source?.image_front_url ||
          source?.image_url ||
          food?.imageUrl ||
          food?.image_url ||
          food?.image
        );

        if (!hasImage && barcode && !processedImageKeysRef.current.has(barcode)) {
          codesToFetch.add(barcode);
        }

        const nameKey = String(food?.name || "").trim().toLowerCase();
        const nameCacheKey = `name:${nameKey}`;

        if (
          !hasImage &&
          !barcode &&
          nameKey.length >= 3 &&
          !processedImageKeysRef.current.has(nameCacheKey)
        ) {
          namesToFetch.add(nameKey);
        }
      });
    });

    const batch = Array.from(codesToFetch).slice(0, 10);

    batch.forEach((code) => {
      if (foodImageFetchRef.current.has(code)) return;

      processedImageKeysRef.current.add(code);

      const request = openFoodFactsService
        .getProductByCode({ code })
        .then((product) => {
          if (product) {
            setFoodImageCache((prev) => ({
              ...prev,
              [code]: product,
            }));
          }
        })
        .catch(() => null)
        .finally(() => {
          foodImageFetchRef.current.delete(code);
        });

      foodImageFetchRef.current.set(code, request);
    });

    const nameBatch = Array.from(namesToFetch).slice(0, 6);

    nameBatch.forEach((nameKey) => {
      if (foodImageSearchRef.current.has(nameKey)) return;

      const nameCacheKey = `name:${nameKey}`;

      processedImageKeysRef.current.add(nameCacheKey);

      const request = openFoodFactsService
        .searchFoods({
          query: nameKey,
          page: 1,
          pageSize: 1,
          filters: { sortBy: "unique_scans_n" },
          allowFuzzy: false,
        })
        .then((data) => {
          const product = (data?.products || [])[0] || null;

          if (product) {
            setFoodImageCache((prev) => ({
              ...prev,
              [nameCacheKey]: product,
            }));
          }
        })
        .catch(() => null)
        .finally(() => {
          foodImageSearchRef.current.delete(nameKey);
        });

      foodImageSearchRef.current.set(nameKey, request);
    });
  }, [filteredMeals, demoData]);

  useEffect(() => {
    const updateViewportMode = () => {
      const isMobile = window.innerWidth <= 1024;
      setIsMobileViewport(isMobile);
      setIsCoarsePointerMode(isCoarsePointerViewport());
      if (!isMobile) {
        setIsMobileHeaderActionsOpen(false);
      }
    };

    updateViewportMode();
    window.addEventListener('resize', updateViewportMode);

    return () => {
      window.removeEventListener('resize', updateViewportMode);
    };
  }, []);

  useEffect(() => {
    if (!Array.isArray(filteredMeals) || filteredMeals.length === 0) {
      setLastActiveMealIndex(0);
      return;
    }
    if (lastActiveMealIndex >= filteredMeals.length) {
      setLastActiveMealIndex(0);
    }
  }, [filteredMeals, lastActiveMealIndex]);

  const isPendingOfflineMeal = useCallback(
    (mealOrId) => {
      if (!mealOrId) {
        return false;
      }

      if (typeof mealOrId === 'string') {
        return journalService.isOfflineMealId(mealOrId);
      }

      return Boolean(mealOrId.isPendingSync) || journalService.isOfflineMealId(mealOrId.id);
    },
    []
  );

  const toDateKey = useCallback((value) => {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const fromDateKey = useCallback((dateKey) => {
    if (!dateKey || typeof dateKey !== 'string') {
      return new Date();
    }
    const [year, month, day] = dateKey.split('-').map(Number);
    if (!year || !month || !day) {
      return new Date();
    }
    return new Date(year, month - 1, day);
  }, []);

  const formatDateShort = useCallback((value) => {
    const date = value instanceof Date ? value : fromDateKey(value);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }, [fromDateKey]);

  const formatTimeShort = useCallback((value) => {
    if (!value) return null;
    if (typeof value === 'string' && /(?:T| )00:00:00/.test(value)) {
      return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    if (date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0) {
      return null;
    }
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  const getDateKey = useCallback(() => {
    return toDateKey(currentDate);
  }, [currentDate, toDateKey]);

  const showToast = useCallback((message) => {
    setShowToastMsg(message);
    setTimeout(() => setShowToastMsg(null), 2600);
  }, []);

  const loadJournalFromAPI = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (demoData) {
        const journalData = demoData.journal || demoData;
        const goals = demoData.goals || {};
        setJournalId(journalData?.id || 'demo-journal');
        setAllMeals(Array.isArray(journalData?.meals) ? journalData.meals : []);
        setNutritionalGoals(goals);
        setSummaryMacros(Array.isArray(goals.summaryMacros) ? goals.summaryMacros : [...SUMMARY_STANDARD_MACROS]);
        setDisplayedMacros(Array.isArray(goals.displayedMacros) ? goals.displayedMacros : []);
        setMainMacros(Array.isArray(goals.mainMacros) ? goals.mainMacros : [...SUMMARY_STANDARD_MACROS]);
        setHideUnavailableFoodMacros(Boolean(goals.hideUnavailableFoodMacros));
        setIsInitialized(true);
        return;
      }

      if (isFriendView) {
        const journalData = await friendsService.getFriendJournalByUsername(friendUsername.trim());
        setJournalId(journalData?.id || null);
        setAllMeals(Array.isArray(journalData?.meals) ? journalData.meals : []);

        try {
          const userJournal = await journalService.loadUserJournal();
          const userGoals = await settingsJournalService.getNutritionalGoals(userJournal.id);
          setNutritionalGoals(userGoals);

          const advancedGoalsEnabled =
            isPremium
            && (
              userGoals.advancedMacroGoalsEnabled === true
              || userGoals.advancedMacroGoalsEnabled === 'true'
            );
          const savedSummaryMacros = Array.isArray(userGoals.summaryMacros) ? userGoals.summaryMacros : [];
          const sanitizedSummaryMacros = Array.from(
            new Set(
              savedSummaryMacros
                .filter((macroKey) => SUMMARY_FOCUSED_MACROS.has(macroKey))
                .filter((macroKey) => advancedGoalsEnabled || !ADVANCED_GOAL_SUMMARY_MACROS.has(macroKey))
            )
          );
          setSummaryMacros(sanitizedSummaryMacros.length > 0 ? sanitizedSummaryMacros : [...SUMMARY_STANDARD_MACROS]);
          setHideUnavailableFoodMacros(Boolean(userGoals.hideUnavailableFoodMacros));

          setDisplayedMacros(userGoals.displayedMacros || []);
          setMainMacros(userGoals.mainMacros || []);
        } catch (err) {
          console.warn('Impossible de charger les paramètres utilisateur pour le journal d\'ami:', err);
        }
      } else {
        const journalData = await journalService.loadUserJournal();
        const pendingOfflineMeals = journalService.getPendingOfflineMeals(journalData.id);
        const mealsWithPending = [...(journalData.meals || []), ...pendingOfflineMeals];

        setJournalId(journalData.id);
        setAllMeals(mealsWithPending);

        const goals = await settingsJournalService.getNutritionalGoals(journalData.id);
        setNutritionalGoals(goals);

        const advancedGoalsEnabled =
          isPremium
          && (
            goals.advancedMacroGoalsEnabled === true
            || goals.advancedMacroGoalsEnabled === 'true'
          );
        const savedSummaryMacros = Array.isArray(goals.summaryMacros) ? goals.summaryMacros : [];
        const sanitizedSummaryMacros = Array.from(
          new Set(
            savedSummaryMacros
              .filter((macroKey) => SUMMARY_FOCUSED_MACROS.has(macroKey))
              .filter((macroKey) => advancedGoalsEnabled || !ADVANCED_GOAL_SUMMARY_MACROS.has(macroKey))
          )
        );
        setSummaryMacros(sanitizedSummaryMacros.length > 0 ? sanitizedSummaryMacros : [...SUMMARY_STANDARD_MACROS]);

        setHideUnavailableFoodMacros(Boolean(goals.hideUnavailableFoodMacros));
      }

      setIsInitialized(true);
    } catch (err) {
      console.error('Erreur chargement journal:', err);
      setError(err.message);
      showToast('Erreur lors du chargement du journal');
    } finally {
      setLoading(false);
    }
  }, [demoData, friendUsername, isFriendView, isPremium, showToast]);

  const filterMealsForCurrentDate = useCallback(() => {
    const dateKey = getDateKey();
    const sortedMeals = [...allMeals].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    const mealsWithFilteredFoods = sortedMeals.map(meal => ({
      ...meal,
      foods: (meal.foods || []).filter(food => {
        if (!food.createdAt) return false;
        const foodDateKey = food.createdAt.split('T')[0];
        return foodDateKey === dateKey;
      })
    }));

    setFilteredMeals(mealsWithFilteredFoods);
  }, [allMeals, getDateKey]);

  useEffect(() => {
    if (!isReadOnly) {
      favoriteFoodsService.listFavorites()
        .then((favorites) => {
          const normalizedFavorites = Array.isArray(favorites) ? favorites : [];
          setFavoriteFoods(normalizedFavorites);
        })
        .catch((error) => {
          console.warn('Impossible de charger les favoris depuis le journal:', error);
        });
    }
  }, [isReadOnly]);

  useEffect(() => {
    loadJournalFromAPI();
  }, [loadJournalFromAPI]);


  useEffect(() => {
    if (isInitialized) {
      filterMealsForCurrentDate();
    }
  }, [currentDate, allMeals, isInitialized, filterMealsForCurrentDate]);

  const syncQueuedMeals = useCallback(async () => {
    if (isReadOnly || !journalId || !isInitialized) {
      return;
    }

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return;
    }

    if (isSyncingQueuedMealsRef.current) {
      return;
    }

    isSyncingQueuedMealsRef.current = true;

    try {
      const { syncedMeals, syncedMutations, failedCount } = await journalService.syncQueuedMeals(journalId);

      if (syncedMeals.length > 0 || syncedMutations > 0) {
        await loadJournalFromAPI();
        const mealPart = syncedMeals.length > 0
          ? `${syncedMeals.length} repas`
          : null;
        const mutationPart = syncedMutations > 0
          ? `${syncedMutations} modification${syncedMutations > 1 ? 's' : ''}`
          : null;
        const summary = [mealPart, mutationPart].filter(Boolean).join(' et ');
        showToast(`${summary} hors ligne synchronisé${syncedMeals.length + syncedMutations > 1 ? 's' : ''}`);
      } else if (failedCount > 0) {
        showToast('Certains repas hors ligne n’ont pas pu être synchronisés.');
      }
    } finally {
      isSyncingQueuedMealsRef.current = false;
    }
  }, [isReadOnly, journalId, isInitialized, loadJournalFromAPI, showToast]);

  useEffect(() => {
    if (isReadOnly || !journalId || !isInitialized) {
      return;
    }

    syncQueuedMeals();
  }, [isReadOnly, journalId, isInitialized, syncQueuedMeals]);

  useEffect(() => {
    const handleOnline = () => {
      showToast('Connexion rétablie. Synchronisation des repas en attente...');
      syncQueuedMeals();
    };

    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [syncQueuedMeals, showToast]);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setModalConfig(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const calculateNutrition = useCallback((baseNutrition, quantity) => {
    const calculated = buildAdjustedNutritionForDisplay(baseNutrition || {}, quantity);
    const kcalValue = calculated.calories ?? calculated['energy-kcal'];

    if (kcalValue !== undefined) {
      calculated.energy = kcalValue;
    }

    return calculated;
  }, []);

  const hasAnyExportableFoods = useMemo(
    () => allMeals.some((meal) => (meal.foods || []).some((food) => Boolean(food?.createdAt))),
    [allMeals]
  );

  const targetCaloriesGoal = nutritionalGoals?.targetCalories;

  const mobileCalorieOverview = useMemo(() => {
    const foodCalories = filteredMeals.reduce((mealSum, meal) => (
      mealSum + (meal.foods || []).reduce((foodSum, food) => {
        const macronutrients = calculateNutrition(food.macronutrients || {}, food.quantity || 100);
        const calories = Number(macronutrients['energy-kcal'] || macronutrients.calories || macronutrients.energy || 0);
        return foodSum + (Number.isFinite(calories) ? calories : 0);
      }, 0)
    ), 0);

    const parsedGoal = Number(targetCaloriesGoal);
    const hasConfiguredGoal = Number.isFinite(parsedGoal) && parsedGoal > 0;
    const goalCalories = hasConfiguredGoal ? parsedGoal : Math.max(foodCalories, 0);
    const remainingCalories = goalCalories - foodCalories;

    return {
      hasConfiguredGoal,
      goalCalories,
      foodCalories,
      remainingCalories,
    };
  }, [filteredMeals, calculateNutrition, targetCaloriesGoal]);

  const calculateNutritionForDisplay = (nutrition100g, quantity) => {
    return buildAdjustedNutritionForDisplay(nutrition100g || {}, quantity);
  };

  const getNextFoodSortOrder = useCallback((meal) => {
    const foods = Array.isArray(meal?.foods) ? meal.foods : [];

    const maxSortOrder = foods.reduce((max, food, index) => {
      const currentSort = Number.isFinite(Number(food?.sortOrder)) ? Number(food.sortOrder) : index;
      return Math.max(max, currentSort);
    }, -1);

    return maxSortOrder + 1;
  }, []);

  const getFoodSelectionKey = useCallback((mealId, food) => {
    if (food?.id !== undefined && food?.id !== null) {
      return `id:${food.id}`;
    }

    const createdAt = String(food?.createdAt || '');
    const name = String(food?.name || '').trim().toLowerCase();
    const quantity = String(food?.quantity ?? '');
    return `tmp:${mealId}:${createdAt}:${name}:${quantity}`;
  }, []);

  const toggleFoodSelection = useCallback((mealId, foodSelectionKey) => {
    const normalizedMealId = String(mealId);
    setSelectedFoodsByMeal((prev) => {
      const currentSelection = prev[normalizedMealId] || [];
      const isSelected = currentSelection.includes(foodSelectionKey);
      const nextSelection = isSelected
        ? currentSelection.filter((item) => item !== foodSelectionKey)
        : [...currentSelection, foodSelectionKey];

      const next = { ...prev };
      if (nextSelection.length > 0) {
        next[normalizedMealId] = nextSelection;
      } else {
        delete next[normalizedMealId];
      }
      return next;
    });
  }, []);

  const clearFoodSelectionForMeal = useCallback((mealId) => {
    const normalizedMealId = String(mealId);
    setSelectedFoodsByMeal((prev) => {
      if (!prev[normalizedMealId]) {
        return prev;
      }
      const next = { ...prev };
      delete next[normalizedMealId];
      return next;
    });
  }, []);

  const selectAllFoodsInMeal = useCallback((mealId) => {
    const normalizedMealId = String(mealId);
    const meal = filteredMeals.find((item) => String(item.id) === normalizedMealId);
    if (!meal) {
      return;
    }

    const allSelectionKeys = (meal.foods || []).map((food) => getFoodSelectionKey(normalizedMealId, food));
    setSelectedFoodsByMeal((prev) => {
      if (allSelectionKeys.length === 0) {
        return prev;
      }
      return {
        ...prev,
        [normalizedMealId]: allSelectionKeys,
      };
    });
  }, [filteredMeals, getFoodSelectionKey]);

  const setBulkMoveTargetForMeal = useCallback((mealId, destinationMealId) => {
    const normalizedMealId = String(mealId);
    const normalizedDestination = destinationMealId ? String(destinationMealId) : '';
    setBulkMoveTargetByMeal((prev) => ({
      ...prev,
      [normalizedMealId]: normalizedDestination,
    }));
  }, []);

  useEffect(() => {
    setSelectedFoodsByMeal((prev) => {
      let changed = false;
      const next = {};

      filteredMeals.forEach((meal) => {
        const mealId = String(meal.id);
        const currentSelection = prev[mealId];
        if (!currentSelection || currentSelection.length === 0) {
          return;
        }

        const validKeys = new Set((meal.foods || []).map((food) => getFoodSelectionKey(mealId, food)));
        const filteredSelection = currentSelection.filter((key) => validKeys.has(key));

        if (filteredSelection.length > 0) {
          next[mealId] = filteredSelection;
        }

        if (filteredSelection.length !== currentSelection.length) {
          changed = true;
        }
      });

      if (!changed && Object.keys(prev).length === Object.keys(next).length) {
        return prev;
      }

      return next;
    });
  }, [filteredMeals, getFoodSelectionKey]);

  useEffect(() => {
    setBulkMoveTargetByMeal((prev) => {
      let changed = false;
      const next = {};

      filteredMeals.forEach((meal) => {
        const mealId = String(meal.id);
        const availableDestinationMeals = filteredMeals.filter(
          (candidateMeal) => String(candidateMeal.id) !== mealId && !isPendingOfflineMeal(candidateMeal)
        );
        const currentTarget = prev[mealId] ? String(prev[mealId]) : '';

        if (availableDestinationMeals.length === 0) {
          if (currentTarget) {
            changed = true;
          }
          return;
        }

        const hasCurrentTarget = currentTarget
          && availableDestinationMeals.some((candidateMeal) => String(candidateMeal.id) === currentTarget);
        const resolvedTarget = hasCurrentTarget
          ? currentTarget
          : String(availableDestinationMeals[0].id);

        if (resolvedTarget !== currentTarget) {
          changed = true;
        }

        next[mealId] = resolvedTarget;
      });

      if (!changed && Object.keys(prev).length === Object.keys(next).length) {
        return prev;
      }

      return next;
    });
  }, [filteredMeals, isPendingOfflineMeal]);

  const reorderMeals = useCallback(async (fromIndex, toIndex) => {
    if (!Number.isInteger(fromIndex) || !Number.isInteger(toIndex) || fromIndex === toIndex) {
      return false;
    }

    if (fromIndex < 0 || toIndex < 0 || fromIndex >= filteredMeals.length || toIndex >= filteredMeals.length) {
      return false;
    }

    const newFilteredMeals = [...filteredMeals];
    const [movedMeal] = newFilteredMeals.splice(fromIndex, 1);
    if (!movedMeal) {
      return false;
    }
    newFilteredMeals.splice(toIndex, 0, movedMeal);

    setFilteredMeals(newFilteredMeals);

    try {
      const mealsWithNewOrder = newFilteredMeals.map((meal, index) => ({
        ...meal,
        sortOrder: index,
      }));

      const result = await journalService.updateMealsOrder(mealsWithNewOrder, journalId);

      setAllMeals((prevMeals) => {
        const updatedMeals = [...prevMeals];
        mealsWithNewOrder.forEach((meal) => {
          const mealIndex = updatedMeals.findIndex((m) => m.id === meal.id);
          if (mealIndex !== -1) {
            updatedMeals[mealIndex] = { ...updatedMeals[mealIndex], sortOrder: meal.sortOrder };
          }
        });
        return updatedMeals;
      });

      showToast(result?.queued ? 'Ordre des repas enregistré (synchronisation en attente).' : 'Ordre des repas sauvegardé');
      return true;
    } catch (error) {
      console.error('Erreur sauvegarde ordre:', error);
      showToast('Erreur lors de la sauvegarde de l\'ordre');
      await loadJournalFromAPI();
      return false;
    }
  }, [filteredMeals, journalId, loadJournalFromAPI, showToast]);

  const moveMealByOffset = useCallback(async (mealIndex, offset) => {
    const targetIndex = mealIndex + offset;
    if (targetIndex < 0 || targetIndex >= filteredMeals.length) {
      return;
    }
    await reorderMeals(mealIndex, targetIndex);
  }, [filteredMeals.length, reorderMeals]);

  const handleDragStart = (e, index) => {
    if (e.target?.closest?.('button, input, select, textarea, a')) {
      e.preventDefault();
      return;
    }

    setDraggedItem(index);
    e.currentTarget.classList.add('dragging');
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = (e) => {
    e?.currentTarget?.classList?.remove('dragging');
    setDraggedItem(null);
    setOverIndex(null);
  };

  const handleDrop = async (e, targetIndex) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === undefined || draggedItem === targetIndex) return;

    await reorderMeals(draggedItem, targetIndex);

    setDraggedItem(null);
    setOverIndex(null);
  };

  const handleFoodDragStart = (e, mealId, foodIndex) => {
    if (e.target?.closest?.('button, input, select, textarea, a')) {
      e.preventDefault();
      return;
    }

    e.stopPropagation();
    setDraggedFood({ mealId: String(mealId), foodIndex });
    e.currentTarget.classList.add('dragging');
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleFoodDragEnd = (e) => {
    e?.stopPropagation();
    e?.currentTarget?.classList?.remove('dragging');
    setDraggedFood(null);
    setFoodOverTarget(null);
  };

  const moveFoodToMealViaDrop = useCallback(async (sourceMealId, sourceFoodIndex, destinationMealId) => {
    const normalizedSourceMealId = String(sourceMealId);
    const normalizedDestinationMealId = String(destinationMealId);

    const sourceMeal = filteredMeals.find((item) => String(item.id) === normalizedSourceMealId);
    const destinationMeal = filteredMeals.find((item) => String(item.id) === normalizedDestinationMealId);

    if (!sourceMeal || !destinationMeal) {
      return false;
    }

    if (isPendingOfflineMeal(sourceMeal) || isPendingOfflineMeal(destinationMeal)) {
      showToast('Repas hors ligne : déplacement indisponible.');
      return false;
    }

    const food = (sourceMeal.foods || [])[sourceFoodIndex];
    if (!food?.id) {
      return false;
    }

    try {
      await journalService.updateFoodItem(food.id, {
        meal: `/api/meals/${normalizedDestinationMealId}`,
        sortOrder: getNextFoodSortOrder(destinationMeal),
      });

      await loadJournalFromAPI();
      showToast(`Aliment déplacé vers "${destinationMeal.name || 'repas'}"`);
      return true;
    } catch (error) {
      console.error('Erreur déplacement aliment (drag & drop) vers un autre repas:', error);
      showToast('Erreur lors du déplacement de l\'aliment');
      return false;
    } finally {
      setDraggedFood(null);
      setFoodOverTarget(null);
    }
  }, [filteredMeals, isPendingOfflineMeal, getNextFoodSortOrder, loadJournalFromAPI, showToast]);

  const persistFoodsOrderForMeal = useCallback(async (mealId, orderedFoods, successMessage = 'Ordre des aliments sauvegardé') => {
    const normalizedMealId = String(mealId);
    const foodsWithSortOrder = (orderedFoods || []).map((food, index) => ({
      ...food,
      sortOrder: index,
    }));

    setFilteredMeals((prevMeals) => prevMeals.map((item) => (
      String(item.id) === normalizedMealId
        ? { ...item, foods: foodsWithSortOrder }
        : item
    )));

    setAllMeals((prevMeals) => prevMeals.map((item) => (
      String(item.id) === normalizedMealId
        ? { ...item, foods: foodsWithSortOrder }
        : item
    )));

    try {
      await journalService.updateFoodItemsOrder(foodsWithSortOrder);
      showToast(successMessage);
      return true;
    } catch (error) {
      console.error('Erreur sauvegarde ordre aliments:', error);
      showToast('Erreur lors de la sauvegarde de l’ordre des aliments');
      await loadJournalFromAPI();
      return false;
    } finally {
      setDraggedFood(null);
      setFoodOverTarget(null);
    }
  }, [loadJournalFromAPI, showToast]);

  const reorderFoodsInMeal = useCallback(async (mealId, fromIndex, toIndex) => {
    if (!Number.isInteger(fromIndex) || !Number.isInteger(toIndex) || fromIndex === toIndex) {
      return false;
    }

    const normalizedMealId = String(mealId);
    const targetMeal = filteredMeals.find((item) => String(item.id) === normalizedMealId);
    if (!targetMeal) {
      return false;
    }

    const currentFoods = [...(targetMeal.foods || [])];
    if (
      fromIndex < 0
      || toIndex < 0
      || fromIndex >= currentFoods.length
      || toIndex >= currentFoods.length
    ) {
      return false;
    }

    const [movedFood] = currentFoods.splice(fromIndex, 1);
    if (!movedFood) {
      return false;
    }

    currentFoods.splice(toIndex, 0, movedFood);
    return persistFoodsOrderForMeal(mealId, currentFoods);
  }, [filteredMeals, persistFoodsOrderForMeal]);

  const handleFoodDrop = async (e, mealId, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedFood) {
      return;
    }

    const destinationMealId = String(mealId);

    if (draggedFood.mealId === destinationMealId) {
      if (draggedFood.foodIndex === targetIndex) return;
      await reorderFoodsInMeal(mealId, draggedFood.foodIndex, targetIndex);
      return;
    }

    await moveFoodToMealViaDrop(draggedFood.mealId, draggedFood.foodIndex, destinationMealId);
  };

  const resetTouchDragState = useCallback(() => {
    if (typeof document !== 'undefined') {
      document.body.classList.remove('touch-drag-active');
    }
    if (touchDragGhostFrameRef.current) {
      window.cancelAnimationFrame(touchDragGhostFrameRef.current);
      touchDragGhostFrameRef.current = null;
    }
    removeTouchDragPreview();

    touchDragStateRef.current = {
      active: false,
      type: null,
      pointerId: null,
      sourceMealIndex: null,
      targetMealIndex: null,
      sourceMealId: null,
      sourceFoodIndex: null,
      targetFoodIndex: null,
    };
    setDraggedItem(null);
    setOverIndex(null);
    setDraggedFood(null);
    setFoodOverTarget(null);
  }, [removeTouchDragPreview]);

  const moveFoodAcrossMeals = useCallback(async (sourceMealId, foodIndex, direction) => {
    const normalizedMealId = String(sourceMealId);
    const mealIdx = filteredMeals.findIndex((item) => String(item.id) === normalizedMealId);
    if (mealIdx === -1) return false;

    const destinationMealIdx = direction === 'down' ? mealIdx + 1 : mealIdx - 1;
    if (destinationMealIdx < 0 || destinationMealIdx >= filteredMeals.length) {
      return false;
    }

    const sourceMeal = filteredMeals[mealIdx];
    const destinationMeal = filteredMeals[destinationMealIdx];

    if (isPendingOfflineMeal(sourceMeal) || isPendingOfflineMeal(destinationMeal)) {
      showToast('Repas hors ligne : déplacement indisponible.');
      return false;
    }

    const food = (sourceMeal.foods || [])[foodIndex];
    if (!food?.id) {
      return false;
    }

    try {
      await journalService.updateFoodItem(food.id, {
        meal: `/api/meals/${destinationMeal.id}`,
        sortOrder: getNextFoodSortOrder(destinationMeal),
      });

      await loadJournalFromAPI();
      showToast(`Aliment déplacé vers "${destinationMeal.name || 'repas'}"`);
      return true;
    } catch (error) {
      console.error('Erreur déplacement aliment vers un autre repas:', error);
      showToast('Erreur lors du déplacement de l\'aliment');
      return false;
    }
  }, [filteredMeals, isPendingOfflineMeal, getNextFoodSortOrder, loadJournalFromAPI, showToast]);

  const moveFoodByOffset = useCallback(async (mealId, foodIndex, offset) => {
    const normalizedMealId = String(mealId);
    const targetMeal = filteredMeals.find((item) => String(item.id) === normalizedMealId);
    const foodsCount = (targetMeal?.foods || []).length;
    const targetIndex = foodIndex + offset;

    if (targetIndex >= 0 && targetIndex < foodsCount) {
      await reorderFoodsInMeal(mealId, foodIndex, targetIndex);
      return;
    }

    await moveFoodAcrossMeals(mealId, foodIndex, offset > 0 ? 'down' : 'up');
  }, [filteredMeals, reorderFoodsInMeal, moveFoodAcrossMeals]);

  const moveSelectedFoodsInMeal = useCallback(async (mealId, direction) => {
    const normalizedMealId = String(mealId);
    const selectedKeys = selectedFoodsByMeal[normalizedMealId] || [];
    if (selectedKeys.length === 0) {
      return false;
    }

    const targetMeal = filteredMeals.find((item) => String(item.id) === normalizedMealId);
    if (!targetMeal) {
      return false;
    }

    const currentFoods = [...(targetMeal.foods || [])];
    if (currentFoods.length < 2) {
      return false;
    }

    const selectedSet = new Set(selectedKeys);
    const isSelectedFood = (food) => selectedSet.has(getFoodSelectionKey(normalizedMealId, food));

    if (!currentFoods.some(isSelectedFood)) {
      clearFoodSelectionForMeal(normalizedMealId);
      return false;
    }

    let nextFoods = [...currentFoods];

    if (direction === 'top') {
      const selectedFoods = currentFoods.filter(isSelectedFood);
      const unselectedFoods = currentFoods.filter((food) => !isSelectedFood(food));
      nextFoods = [...selectedFoods, ...unselectedFoods];
    } else if (direction === 'bottom') {
      const selectedFoods = currentFoods.filter(isSelectedFood);
      const unselectedFoods = currentFoods.filter((food) => !isSelectedFood(food));
      nextFoods = [...unselectedFoods, ...selectedFoods];
    } else if (direction === 'up') {
      for (let index = 1; index < nextFoods.length; index += 1) {
        if (isSelectedFood(nextFoods[index]) && !isSelectedFood(nextFoods[index - 1])) {
          const temp = nextFoods[index - 1];
          nextFoods[index - 1] = nextFoods[index];
          nextFoods[index] = temp;
        }
      }
    } else if (direction === 'down') {
      for (let index = nextFoods.length - 2; index >= 0; index -= 1) {
        if (isSelectedFood(nextFoods[index]) && !isSelectedFood(nextFoods[index + 1])) {
          const temp = nextFoods[index + 1];
          nextFoods[index + 1] = nextFoods[index];
          nextFoods[index] = temp;
        }
      }
    } else {
      return false;
    }

    const currentOrder = currentFoods.map((food) => getFoodSelectionKey(normalizedMealId, food)).join('||');
    const nextOrder = nextFoods.map((food) => getFoodSelectionKey(normalizedMealId, food)).join('||');

    if (currentOrder === nextOrder) {
      return false;
    }

    const selectedCount = nextFoods.filter(isSelectedFood).length;
    const successMessage = selectedCount > 1
      ? `${selectedCount} aliments réorganisés`
      : 'Aliment réorganisé';

    return persistFoodsOrderForMeal(mealId, nextFoods, successMessage);
  }, [clearFoodSelectionForMeal, filteredMeals, getFoodSelectionKey, persistFoodsOrderForMeal, selectedFoodsByMeal]);

  const getSelectedFoodsForMeal = useCallback((mealId) => {
    const normalizedMealId = String(mealId);
    const meal = filteredMeals.find((item) => String(item.id) === normalizedMealId);
    if (!meal) {
      return { meal: null, selectedFoods: [] };
    }

    const selectedKeys = selectedFoodsByMeal[normalizedMealId] || [];
    if (selectedKeys.length === 0) {
      return { meal, selectedFoods: [] };
    }

    const selectedKeySet = new Set(selectedKeys);
    const selectedFoods = (meal.foods || []).filter((food) => (
      selectedKeySet.has(getFoodSelectionKey(normalizedMealId, food))
    ));

    return { meal, selectedFoods };
  }, [filteredMeals, getFoodSelectionKey, selectedFoodsByMeal]);

  const deleteSelectedFoodsInMeal = useCallback((mealId) => {
    const { meal, selectedFoods } = getSelectedFoodsForMeal(mealId);
    if (!meal || selectedFoods.length === 0) {
      return;
    }

    const deletableFoods = selectedFoods.filter((food) => food?.id);
    if (deletableFoods.length === 0) {
      showToast('Aucun aliment sélectionné ne peut être supprimé.');
      return;
    }

    const count = deletableFoods.length;
    setModalConfig({
      title: count > 1 ? `Supprimer ${count} aliments ?` : 'Supprimer cet aliment ?',
      message: count > 1
        ? 'Les aliments sélectionnés seront supprimés de ce repas.'
        : `L'aliment "${deletableFoods[0]?.name || 'sélectionné'}" sera supprimé de ce repas.`,
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      confirmVariant: 'danger',
      size: 'sm',
      onConfirm: async () => {
        try {
          await Promise.all(deletableFoods.map((food) => journalService.deleteFoodItem(food.id)));
          clearFoodSelectionForMeal(mealId);
          closeModal();
          await loadJournalFromAPI();
          showToast(count > 1 ? `${count} aliments supprimés` : 'Aliment supprimé');
        } catch (error) {
          console.error('Erreur suppression multiple aliments:', error);
          closeModal();
          showToast('Erreur lors de la suppression des aliments');
        }
      },
      onCancel: closeModal
    });
    setShowModal(true);
  }, [clearFoodSelectionForMeal, closeModal, getSelectedFoodsForMeal, loadJournalFromAPI, showToast]);

  const moveSelectedFoodsToMeal = useCallback(async (sourceMealId, destinationMealId) => {
    const sourceId = String(sourceMealId);
    const destinationId = String(destinationMealId || '').trim();
    if (!destinationId) {
      showToast('Sélectionnez un repas destination.');
      return false;
    }

    if (sourceId === destinationId) {
      showToast('Choisissez un autre repas pour le déplacement.');
      return false;
    }

    const { meal: sourceMeal, selectedFoods } = getSelectedFoodsForMeal(sourceId);
    if (!sourceMeal || selectedFoods.length === 0) {
      showToast('Aucun aliment sélectionné à déplacer.');
      return false;
    }

    const destinationMeal = filteredMeals.find((item) => String(item.id) === destinationId);
    if (!destinationMeal) {
      showToast('Repas destination introuvable.');
      return false;
    }

    if (isPendingOfflineMeal(destinationMeal)) {
      showToast('Le repas destination est hors ligne, déplacement indisponible.');
      return false;
    }

    const movableFoods = selectedFoods.filter((food) => Boolean(food?.id));
    if (movableFoods.length === 0) {
      showToast('Les aliments sélectionnés ne peuvent pas être déplacés.');
      return false;
    }

    let nextSortOrder = getNextFoodSortOrder(destinationMeal);

    try {
      for (const food of movableFoods) {
        await journalService.updateFoodItem(food.id, {
          meal: `/api/meals/${destinationId}`,
          sortOrder: nextSortOrder,
        });
        nextSortOrder += 1;
      }

      clearFoodSelectionForMeal(sourceId);
      await loadJournalFromAPI();

      const skippedCount = selectedFoods.length - movableFoods.length;
      const destinationLabel = destinationMeal.name || 'le repas destination';
      const baseMessage = movableFoods.length > 1
        ? `${movableFoods.length} aliments déplacés vers ${destinationLabel}`
        : `Aliment déplacé vers ${destinationLabel}`;

      showToast(skippedCount > 0 ? `${baseMessage} (${skippedCount} ignoré(s))` : baseMessage);
      return true;
    } catch (error) {
      console.error('Erreur déplacement multiple aliments:', error);
      showToast('Erreur lors du déplacement des aliments');
      return false;
    }
  }, [clearFoodSelectionForMeal, filteredMeals, getNextFoodSortOrder, getSelectedFoodsForMeal, isPendingOfflineMeal, loadJournalFromAPI, showToast]);

  const Toast = () => {
    if (!showToastMsg) return null;

    return (
      <Alert
        type="info"
        message={showToastMsg}
        duration={3000}
        onClose={() => setShowToastMsg(null)}
      />
    );
  };

  const renderMealElement = (meal, index) => {
    const mealTimeValue = String(meal.mealTime || '').slice(0, 5);
    let totals = {};
    totals['proteins'] = 0;
    totals['carbohydrates'] = 0;
    totals['fat'] = 0;
    totals['energy'] = 0;
    totals['sodium'] = 0;
    totals['sugars'] = 0;

    (meal.foods || []).forEach(food => {
      const actualNutrition = calculateNutrition(food.macronutrients || {}, food.quantity || 100);
      totals['proteins'] += actualNutrition['proteins'] || 0;
      totals['carbohydrates'] += actualNutrition['carbohydrates'] || 0;
      totals['fat'] += actualNutrition['fat'] || 0;
      totals['energy'] += actualNutrition['energy'] || 0;
      totals['sodium'] += actualNutrition['sodium'] || 0;
      totals['sugars'] += actualNutrition['sugars'] || 0;
    });

    const mealCalories = Math.round(totals.energy);
    const mealIsPendingSync = isPendingOfflineMeal(meal);
    const isEditingName = editingMealId === meal.id;
    const mealNameValue = isEditingName
      ? (mealNameDrafts[meal.id] ?? meal.name ?? '')
      : (meal.name ?? '');
    const isMealActionsOpen = isMobileViewport && (openMealActionsId === meal.id || isEditingName);

    const toggleFoodIngredients = (foodKey) => {
      setCollapsedFoodIngredients((prev) => {
        const currentlyCollapsed = prev[foodKey] !== false;
        return {
          ...prev,
          [foodKey]: !currentlyCollapsed,
        };
      });
    };

    const formatGramCell = (value) => {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) {
        return '0';
      }
      if (Math.abs(numeric) >= 10) {
        return `${Math.round(numeric)}`;
      }
      return numeric.toFixed(1).replace(/\.0$/, '');
    };

    const formatSodiumMgCell = (valueInGrams) => {
      const numeric = Number(valueInGrams);
      if (!Number.isFinite(numeric)) {
        return '0';
      }
      const milligrams = numeric * 1000;
      if (Math.abs(milligrams) >= 10) {
        return `${Math.round(milligrams)}`;
      }
      return milligrams.toFixed(1).replace(/\.0$/, '');
    };

    const useDesktopLayoutOnMobile = false;
    const canDragOnCurrentViewport = !isMobileViewport;
    const mealDragEnabled = canDragOnCurrentViewport && !mealIsPendingSync;
    const nativeHtmlDragEnabled = mealDragEnabled && !isCoarsePointerMode;
    const selectedFoodKeysForMeal = selectedFoodsByMeal[String(meal.id)] || [];
    const selectedFoodsCountForMeal = selectedFoodKeysForMeal.length;
    const hasSelectedFoodsInMeal = selectedFoodsCountForMeal > 0;
    const destinationMealsForSelection = filteredMeals.filter(
      (candidateMeal) => String(candidateMeal.id) !== String(meal.id) && !isPendingOfflineMeal(candidateMeal)
    );
    const selectedDestinationMealId = bulkMoveTargetByMeal[String(meal.id)]
      || (destinationMealsForSelection[0] ? String(destinationMealsForSelection[0].id) : '');

    if (!isMobileViewport || useDesktopLayoutOnMobile) {
      return (
        <div
          key={meal.id}
          className={`meal-section meal-table-section ${mealIsPendingSync ? 'pending-sync' : ''} ${draggedItem === index ? 'dragging' : ''} ${overIndex === index ? 'over' : ''}`}
          data-meal-dnd-index={index}
          draggable={nativeHtmlDragEnabled}
          onDragOver={(e) => {
            if (draggedFood) {
              if (mealIsPendingSync) return;
              e.preventDefault();
              return;
            }
            if (!mealDragEnabled) return;
            e.preventDefault();
            setOverIndex(index);
          }}
          onDragLeave={() => setOverIndex(null)}
          onDrop={(e) => {
            if (draggedFood) {
              e.preventDefault();
              if (mealIsPendingSync) return;
              handleFoodDrop(e, meal.id, (meal.foods || []).length);
              return;
            }
            if (!mealDragEnabled) return;
            handleDrop(e, index);
          }}
          onDragEnd={(e) => {
            if (!mealDragEnabled) return;
            handleDragEnd(e);
          }}
        >
          <div className="meal-table-toolbar">
            <div className="meal-title-section">
              {!isReadOnly && (
                <span
                  className="meal-title-drag-handle"
                  title="Glisser le repas pour le déplacer"
                  aria-hidden="true"
                >
                  <FontAwesomeIcon icon={faGripVertical} />
                </span>
              )}
              <input
                type="text"
                value={mealNameValue}
                className="editable-title meal-table-title-input"
                placeholder="Nom du repas"
                aria-label="Nom du repas"
                readOnly={!isEditingName}
                ref={(el) => {
                  mealNameInputRefs.current[meal.id] = el;
                }}
              />
              {mealIsPendingSync && (
                <span className="meal-pending-badge" title="Ce repas sera synchronisé dès que la connexion revient">
                  Hors ligne · en attente
                </span>
              )}
            </div>
            <time
              className="meal-time"
              dateTime={mealTimeValue || undefined}
              aria-label={mealTimeValue ? `Heure du repas : ${mealTimeValue}` : 'Heure du repas non renseignée'}
              title={mealTimeValue ? `Heure du repas : ${mealTimeValue}` : 'Heure du repas non renseignée'}
            >
              <FontAwesomeIcon icon={faClock} aria-hidden="true" />
              {mealTimeValue || '—'}
            </time>
            {!isReadOnly && (
              <div className="meal-table-toolbar-actions">
                <button
                  type="button"
                  draggable={false}
                  title="Copier ce repas"
                  aria-label={`Copier le repas ${meal.name || ''}`.trim()}
                  className="meal-copy-btn-text"
                >
                  <FontAwesomeIcon icon={faCopy} />
                  Copier
                </button>
                <button
                  type="button"
                  draggable={false}
                  title={isEditingName ? 'Valider le nouveau nom' : 'Renommer ce repas'}
                  aria-label={isEditingName ? `Valider le nom du repas ${meal.name || ''}`.trim() : `Renommer le repas ${meal.name || ''}`.trim()}
                  className="meal-rename-btn-text"
                >
                  <FontAwesomeIcon icon={isEditingName ? faCheck : faPen} />
                  {isEditingName ? 'Valider' : 'Renommer'}
                </button>
                {isEditingName && (
                  <button
                    type="button"
                    draggable={false}
                    title="Annuler la modification"
                    aria-label={`Annuler le renommage du repas ${meal.name || ''}`.trim()}
                    className="meal-cancel-btn-text"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                    Annuler
                  </button>
                )}
                <button
                  type="button"
                  draggable={false}
                  title="Supprimer ce repas"
                  aria-label={`Supprimer le repas ${meal.name || ''}`.trim()}
                  className="meal-delete-btn-text"
                >
                  <FontAwesomeIcon icon={faTrash} />
                  Supprimer
                </button>
              </div>
            )}
          </div>
          {!isReadOnly && hasSelectedFoodsInMeal && (
            <div className="meal-table-selection-toolbar" onClick={(event) => event.stopPropagation()}>
              <span className="meal-table-selection-count">
                {selectedFoodsCountForMeal} aliment{selectedFoodsCountForMeal > 1 ? 's' : ''} sélectionné{selectedFoodsCountForMeal > 1 ? 's' : ''}
              </span>
              <div className="meal-table-selection-actions">
                <button
                  type="button"
                  className="meal-table-selection-btn subtle"
                  onClick={() => selectAllFoodsInMeal(meal.id)}
                  disabled={mealIsPendingSync}
                  title="Sélectionner tous les aliments du repas"
                >
                  Tout sélectionner
                </button>
                <button
                  type="button"
                  className="meal-table-selection-btn"
                  onClick={() => moveSelectedFoodsInMeal(meal.id, 'top')}
                  disabled={mealIsPendingSync}
                  title="Placer la sélection en début de repas"
                >
                  Début
                </button>
                <button
                  type="button"
                  className="meal-table-selection-btn"
                  onClick={() => moveSelectedFoodsInMeal(meal.id, 'up')}
                  disabled={mealIsPendingSync}
                  title="Monter la sélection"
                >
                  <FontAwesomeIcon icon={faArrowUp} />
                  Monter
                </button>
                <button
                  type="button"
                  className="meal-table-selection-btn"
                  onClick={() => moveSelectedFoodsInMeal(meal.id, 'down')}
                  disabled={mealIsPendingSync}
                  title="Descendre la sélection"
                >
                  <FontAwesomeIcon icon={faArrowDown} />
                  Descendre
                </button>
                <button
                  type="button"
                  className="meal-table-selection-btn"
                  onClick={() => moveSelectedFoodsInMeal(meal.id, 'bottom')}
                  disabled={mealIsPendingSync}
                  title="Placer la sélection en fin de repas"
                >
                  Fin
                </button>
                <button
                  type="button"
                  className="meal-table-selection-btn subtle"
                  onClick={() => clearFoodSelectionForMeal(meal.id)}
                  title="Effacer la sélection"
                >
                  Désélectionner
                </button>
                <button
                  type="button"
                  className="meal-table-selection-btn danger"
                  onClick={() => deleteSelectedFoodsInMeal(meal.id)}
                  disabled={mealIsPendingSync}
                  title="Supprimer la sélection"
                >
                  <FontAwesomeIcon icon={faTrash} />
                  Supprimer
                </button>
                <div className="meal-table-selection-move-group">
                  <select
                    className="meal-table-selection-select"
                    value={selectedDestinationMealId}
                    onChange={(event) => setBulkMoveTargetForMeal(meal.id, event.target.value)}
                    disabled={mealIsPendingSync || destinationMealsForSelection.length === 0}
                    aria-label="Choisir le repas destination"
                  >
                    {destinationMealsForSelection.length === 0 ? (
                      <option value="">Aucun repas destination</option>
                    ) : (
                      destinationMealsForSelection.map((candidateMeal) => (
                        <option key={candidateMeal.id} value={candidateMeal.id}>
                          {candidateMeal.name || 'Repas'}
                        </option>
                      ))
                    )}
                  </select>
                  <button
                    type="button"
                    className="meal-table-selection-btn"
                    onClick={() => moveSelectedFoodsToMeal(meal.id, selectedDestinationMealId)}
                    disabled={mealIsPendingSync || !selectedDestinationMealId || destinationMealsForSelection.length === 0}
                    title="Déplacer la sélection vers le repas choisi"
                  >
                    Déplacer vers
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="meal-table-wrapper">
            <table className="meal-table">
              <thead>
                <tr>
                  <th className="food-col">Aliment</th>
                  <th>Calories<br /><span>kcal</span></th>
                  <th>Glucides<br /><span>g</span></th>
                  <th>Lipides<br /><span>g</span></th>
                  <th>Protéines<br /><span>g</span></th>
                  <th>Sodium<br /><span>mg</span></th>
                  <th>Sucres<br /><span>g</span></th>
                  {!isReadOnly && (
                    <th className="action-col">
                      Actions
                      <br />
                      <span aria-hidden="true">&nbsp;</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {(meal.foods || []).length === 0 ? (
                  <tr
                    className="meal-table-empty"
                    onDragOver={(e) => {
                      if (mealIsPendingSync) return;
                      e.preventDefault();
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (mealIsPendingSync || !draggedFood) return;
                      handleFoodDrop(e, meal.id, 0);
                    }}
                  >
                    <td colSpan={mealTableColSpan}>
                      <div className="no-food-message meal-table-empty-shared">
                        <p>Aucun aliment</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  (meal.foods || []).map((food, fIndex) => {
                    const nutrition = calculateNutrition(food.macronutrients || {}, food.quantity || 100);
                    const foodIngredientsKey = String(food.id || `${meal.id}-${fIndex}`);
                    const foodSelectionKey = getFoodSelectionKey(meal.id, food);
                    const isFoodSelected = selectedFoodKeysForMeal.includes(foodSelectionKey);
                    const foodIngredients = Array.isArray(food.ingredients)
                      ? food.ingredients.filter((ingredient) => String(ingredient || '').trim().length > 0)
                      : [];
                    const detailedNutrition = calculateNutritionForDisplay(food.macronutrients, food.quantity);
                    const detailMacrosForFood = allDetailMacroKeys;
                    const secondaryMacrosForFood = detailMacrosForFood.filter(
                      (macroKey) => !foodPreviewMacros.includes(macroKey)
                    );
                    const hasDetailedMacros = hideUnavailableFoodMacros
                      ? detailMacrosForFood.some((macroKey) => {
                        const value = detailedNutrition[macroKey];
                        const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
                        return Number.isFinite(parsed);
                      })
                      : detailMacrosForFood.length > 0;
                    const hasIngredients = foodIngredients.length > 0;
                    const hasNote = Boolean(food.note && food.note.trim());
                    const hasDetails = hasDetailedMacros || hasIngredients || hasNote || !isPremium;
                    const isDetailsCollapsed = collapsedFoodIngredients[foodIngredientsKey] !== false;
                    const rawOffData = food?.openFoodFactsData
                      || food?.openfoodfactsData
                      || food?.openFoodFacts
                      || food?.offData
                      || food?.sourceData
                      || food?.source_data
                      || null;
                    const offData = parseFoodSourceData(rawOffData);
                    const barcodeKey = getFoodBarcode(food, offData);
                    const foodDisplayName = food.name;
                    const nameKey = foodDisplayName.trim().toLowerCase();
                    const cachedProduct = barcodeKey ? foodImageCache[barcodeKey] : null;
                    const cachedNameProduct = nameKey ? foodImageCache[`name:${nameKey}`] : null;
                    const cachedSourceImage = cachedProduct?.image_front_small_url
                      || cachedProduct?.image_thumb_url
                      || cachedProduct?.image_front_url
                      || cachedProduct?.image_url
                      || cachedNameProduct?.image_front_small_url
                      || cachedNameProduct?.image_thumb_url
                      || cachedNameProduct?.image_front_url
                      || cachedNameProduct?.image_url
                      || '';
                    const rawFoodImageUrl = cachedSourceImage
                      || offData?.image_front_small_url
                      || offData?.image_thumb_url
                      || offData?.image_front_url
                      || offData?.image_url
                      || food?.imageUrl
                      || food?.image_url
                      || food?.image
                      || '';
                    const foodImageUrl = String(rawFoodImageUrl || '').trim();
                    const hasFoodImage = Boolean(foodImageUrl)
                      && foodImageUrl !== 'null'
                      && foodImageUrl !== 'undefined';
                    const fallbackInitial = String(foodDisplayName || '?').trim().charAt(0).toUpperCase();
                    const foodTime = formatTimeShort(food.createdAt);
                    const favoritePayload = buildFavoriteFoodPayload({ food, displayName: foodDisplayName, sourceData: offData });
                    const isFavoriteFood = favoriteFoods.some((favorite) => matchesFavoriteFood(favorite, favoritePayload));

                    return (
                      <React.Fragment key={food.id || `${meal.id}-${fIndex}`}>
                        <tr
                          className={`meal-table-row ${isFoodSelected ? 'selected' : ''} ${draggedFood?.mealId === String(meal.id) && draggedFood?.foodIndex === fIndex ? 'dragging' : ''} ${foodOverTarget?.mealId === String(meal.id) && foodOverTarget?.foodIndex === fIndex ? 'over' : ''} ${hasDetails && !isDetailsCollapsed ? 'details-open' : ''}`}
                          data-food-dnd-meal-id={String(meal.id)}
                          data-food-dnd-index={fIndex}
                          draggable={nativeHtmlDragEnabled && !mealIsPendingSync}
                          onDragStart={(e) => {
                            if (!mealDragEnabled) {
                              e.preventDefault();
                              return;
                            }
                            handleFoodDragStart(e, meal.id, fIndex);
                          }}
                          onDragEnd={(e) => {
                            if (!mealDragEnabled) return;
                            handleFoodDragEnd(e);
                          }}
                          onDragOver={(e) => {
                            if (!mealDragEnabled) return;
                            e.preventDefault();
                            e.stopPropagation();
                            setFoodOverTarget({ mealId: String(meal.id), foodIndex: fIndex });
                          }}
                          onDragLeave={(e) => {
                            e.stopPropagation();
                            if (foodOverTarget?.mealId === String(meal.id) && foodOverTarget?.foodIndex === fIndex) {
                              setFoodOverTarget(null);
                            }
                          }}
                          onDrop={(e) => {
                            if (!mealDragEnabled) return;
                            handleFoodDrop(e, meal.id, fIndex);
                          }}
                          aria-expanded={hasDetails ? !isDetailsCollapsed : undefined}
                        >
                          <td className="food-name-cell">
                            <div className="food-name-with-image">
                              {!isReadOnly && (
                                <label
                                  className="meal-table-select-toggle"
                                  onClick={(event) => event.stopPropagation()}
                                  title={isFoodSelected ? 'Retirer de la sélection' : 'Ajouter à la sélection'}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isFoodSelected}
                                    onChange={() => toggleFoodSelection(meal.id, foodSelectionKey)}
                                    aria-label={`${isFoodSelected ? 'Retirer' : 'Ajouter'} ${foodDisplayName || 'cet aliment'} de la sélection`}
                                    disabled={mealIsPendingSync}
                                  />
                                </label>
                              )}
                              {!isReadOnly && (
                                <span
                                  className="meal-table-drag-handle"
                                  title="Glisser pour déplacer"
                                  aria-hidden="true"
                                >
                                  <FontAwesomeIcon icon={faGripVertical} />
                                </span>
                              )}
                              {hasFoodImage ? (
                                <button
                                  type="button"
                                  className="meal-table-image-btn"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openFoodImagePreview({
                                      food,
                                      sourceData: offData,
                                      cachedProduct,
                                      cachedNameProduct,
                                      imageUrl: foodImageUrl,
                                      displayName: foodDisplayName,
                                      barcode: barcodeKey,
                                    });
                                  }}
                                  aria-label={`Agrandir l'image de ${foodDisplayName || 'cet aliment'}`}
                                >
                                  <img
                                    src={foodImageUrl}
                                    alt={foodDisplayName || 'Aliment'}
                                    className="meal-table-food-image"
                                    loading="lazy"
                                    onError={(event) => {
                                      event.currentTarget.style.display = 'none';
                                    }}
                                  />
                                </button>
                              ) : (
                                <div className="meal-table-food-image placeholder">{fallbackInitial}</div>
                              )}
                              <div className="food-name-content">
                                <div className="food-name-main">{foodDisplayName || 'Aliment'}</div>
                                <div className="food-name-sub">
                                  {Math.round(Number(food.quantity) || 100)} g
                                  {foodTime ? <span className="food-added-inline">• {foodTime}</span> : null}
                                </div>
                                {hasDetails && (
                                  <button
                                    type="button"
                                    className={`food-row-hint ${isDetailsCollapsed ? '' : 'open'}`}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      toggleFoodIngredients(foodIngredientsKey);
                                    }}
                                    aria-label={isDetailsCollapsed ? 'Afficher les détails de l’aliment' : 'Masquer les détails de l’aliment'}
                                  >
                                    <FontAwesomeIcon icon={faChevronDown} />
                                    {isDetailsCollapsed ? 'Détails' : 'Masquer'}
                                  </button>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>{Math.round(Number(nutrition.energy || nutrition['energy-kcal'] || 0))}</td>
                          <td>{formatGramCell(nutrition.carbohydrates)}</td>
                          <td>{formatGramCell(nutrition.fat)}</td>
                          <td>{formatGramCell(nutrition.proteins)}</td>
                          <td>{formatSodiumMgCell(nutrition.sodium)}</td>
                          <td>{formatGramCell(nutrition.sugars)}</td>
                          {!isReadOnly && (
                            <td className="meal-table-actions-cell">
                              {!isReadOnly && (
                                <div className="meal-table-actions" onClick={(event) => event.stopPropagation()}>
                                  <button
                                    type="button"
                                    className="food-mobile-action-btn note"
                                    disabled={mealIsPendingSync}
                                    title="Ajouter/modifier une note"
                                    aria-label={`Modifier la note de ${foodDisplayName || 'cet aliment'}`}
                                  >
                                    <FontAwesomeIcon icon={faStickyNote} />
                                    Note
                                  </button>
                                  <button
                                    type="button"
                                    className="food-mobile-action-btn edit"
                                    disabled={mealIsPendingSync}
                                    title="Modifier cet aliment"
                                    aria-label={`Modifier ${foodDisplayName || 'cet aliment'}`}
                                  >
                                    <FontAwesomeIcon icon={faPen} />
                                    Éditer
                                  </button>
                                  <button
                                    type="button"
                                    className={`food-mobile-action-btn favorite ${isFavoriteFood ? 'active' : ''}`}
                                    disabled={mealIsPendingSync}
                                    title={isFavoriteFood ? `Retirer ${foodDisplayName || 'cet aliment'} des favoris` : `Ajouter ${foodDisplayName || 'cet aliment'} aux favoris`}
                                    aria-label={isFavoriteFood ? `Retirer ${foodDisplayName || 'cet aliment'} des favoris` : `Ajouter ${foodDisplayName || 'cet aliment'} aux favoris`}
                                  >
                                    <FontAwesomeIcon icon={faStar} />
                                    {isFavoriteFood ? 'Retirer' : 'Ajouter'}
                                  </button>
                                  <button
                                    type="button"
                                    className="food-mobile-action-btn delete"
                                    disabled={mealIsPendingSync}
                                    title="Supprimer cet aliment"
                                    aria-label={`Supprimer ${foodDisplayName || 'cet aliment'}`}
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                    Supprimer
                                  </button>
                                </div>
                              )}
                            </td>
                          )}
                        </tr>
                        {hasDetails && !isDetailsCollapsed && (
                          <tr className="meal-table-details-row">
                            <td colSpan={mealTableColSpan}>
                              <div className="meal-table-details-content">
                                {hasNote && (
                                  <div className="meal-table-note">
                                    <div className="meal-table-note-label">
                                      <FontAwesomeIcon icon={faStickyNote} />
                                      Note
                                    </div>
                                    <p>{food.note}</p>
                                  </div>
                                )}

                                {hasDetailedMacros && (
                                  <div className="meal-table-details-macros">
                                    <FoodMacros
                                      macronutrients={detailedNutrition}
                                      mainMacros={foodPreviewMacros}
                                      displayedMacros={[]}
                                      showToggle={false}
                                      hideUnavailableMacros={hideUnavailableFoodMacros}
                                    />
                                    {secondaryMacrosForFood.length > 0 && (
                                      <FoodMacros
                                        macronutrients={detailedNutrition}
                                        mainMacros={[]}
                                        displayedMacros={secondaryMacrosForFood}
                                        renderMainMacros={false}
                                        expanded
                                        showToggle={false}
                                        hideUnavailableMacros={hideUnavailableFoodMacros}
                                        showSectionTitle
                                      />
                                    )}
                                  </div>
                                )}

                                {hasIngredients ? (
                                  <div className="food-ingredients-block">
                                    <div className="food-ingredients-title">Ingrédients</div>
                                    <div className="food-ingredients">
                                      <div className="food-ingredients-list">
                                        {foodIngredients.map((ingredient, ingredientIndex) => (
                                          <span
                                            key={`${foodIngredientsKey}-ingredient-${ingredientIndex}`}
                                            className="food-ingredient-chip"
                                          >
                                            {ingredient}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  }))}
              </tbody>
              <tfoot>
                <tr className="meal-table-total-row">
                  <td className="meal-table-total-title-cell">
                    <span className="meal-table-total-title">Total du repas</span>
                  </td>
                  <td>
                    <span className="meal-total-pill meal-total-pill-calories" title="Calories totales du repas">
                      <span className="meal-total-value">{Math.round(totals.energy)}</span>
                      <span className="meal-total-unit">kcal</span>
                    </span>
                  </td>
                  <td>
                    <span className="meal-total-pill meal-total-pill-carbs" title="Glucides totaux du repas">
                      <span className="meal-total-value">{formatGramCell(totals.carbohydrates)}</span>
                      <span className="meal-total-unit">g</span>
                    </span>
                  </td>
                  <td>
                    <span className="meal-total-pill meal-total-pill-fat" title="Lipides totaux du repas">
                      <span className="meal-total-value">{formatGramCell(totals.fat)}</span>
                      <span className="meal-total-unit">g</span>
                    </span>
                  </td>
                  <td>
                    <span className="meal-total-pill meal-total-pill-proteins" title="Protéines totales du repas">
                      <span className="meal-total-value">{formatGramCell(totals.proteins)}</span>
                      <span className="meal-total-unit">g</span>
                    </span>
                  </td>
                  <td>
                    <span className="meal-total-pill meal-total-pill-sodium" title="Sodium total du repas">
                      <span className="meal-total-value">{formatSodiumMgCell(totals.sodium)}</span>
                      <span className="meal-total-unit">mg</span>
                    </span>
                  </td>
                  <td>
                    <span className="meal-total-pill meal-total-pill-sugars" title="Sucres totaux du repas">
                      <span className="meal-total-value">{formatGramCell(totals.sugars)}</span>
                      <span className="meal-total-unit">g</span>
                    </span>
                  </td>
                  {!isReadOnly && <td></td>}
                </tr>
              </tfoot>
            </table>
            <div className="meal-table-footer-actions">
              <div className="meal-table-link-group">
                {!isReadOnly && (
                  <>
                    <button
                      type="button"
                      className="meal-table-link meal-table-link-pill"
                      disabled={mealIsPendingSync}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      Ajouter un aliment
                    </button>
                    <button
                      type="button"
                      className="meal-table-link meal-table-link-pill search"
                      disabled={mealIsPendingSync}
                    >
                      <FontAwesomeIcon icon={faMagnifyingGlass} />
                      Rechercher un aliment
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        key={meal.id}
        className={`meal-section mobile-diary-meal ${mealIsPendingSync ? 'pending-sync' : ''} ${draggedItem === index ? 'dragging' : ''} ${overIndex === index ? 'over' : ''}`}
        data-meal-dnd-index={index}
        draggable={nativeHtmlDragEnabled}
      >
        <div
          className="meal-header"
        >
          <div className="meal-title-section">
            <input
              type="text"
              value={mealNameValue}
              className="editable-title"
              placeholder="Nom du repas"
              aria-label="Nom du repas"
              readOnly={!isEditingName}
              ref={(el) => {
                mealNameInputRefs.current[meal.id] = el;
              }}
            />
            {mealIsPendingSync && (
              <span className="meal-pending-badge" title="Ce repas sera synchronisé dès que la connexion revient">
                Hors ligne · en attente
              </span>
            )}
          </div>
          <time
            className="meal-time"
            dateTime={mealTimeValue || undefined}
            aria-label={mealTimeValue ? `Heure du repas : ${mealTimeValue}` : 'Heure du repas non renseignée'}
            title={mealTimeValue ? `Heure du repas : ${mealTimeValue}` : 'Heure du repas non renseignée'}
          >
            <FontAwesomeIcon icon={faClock} aria-hidden="true" />
            {mealTimeValue || '—'}
          </time>
          {isMobileViewport ? (
            <div className="meal-mobile-header-tools">
              <div className="meal-mobile-sort-controls">
                <button
                  type="button"
                  className="meal-mobile-sort-btn"
                  disabled={mealIsPendingSync || index === 0}
                  aria-label={`Monter le repas ${meal.name || ''}`.trim()}
                  title="Monter ce repas"
                >
                  <FontAwesomeIcon icon={faArrowUp} />
                </button>
                <button
                  type="button"
                  className="meal-mobile-sort-btn"
                  disabled={mealIsPendingSync || index === filteredMeals.length - 1}
                  aria-label={`Descendre le repas ${meal.name || ''}`.trim()}
                  title="Descendre ce repas"
                >
                  <FontAwesomeIcon icon={faArrowDown} />
                </button>
              </div>
              {!isReadOnly && (
                <button
                  type="button"
                  className={`meal-actions-toggle ${isMealActionsOpen ? 'active' : ''}`}
                  aria-label={`Actions pour le repas ${meal.name || ''}`.trim()}
                >
                  Actions
                  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                    <path
                      d="M6.5 9.5 12 15l5.5-5.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
            </div>
          ) : (
            <div className="meal-actions">
              <button
                type="button"
                draggable={false}
                title="Copier ce repas"
                aria-label={`Copier le repas ${meal.name || ''}`.trim()}
                className="meal-copy-btn-text"
              >
                <FontAwesomeIcon icon={faCopy} />
                Copier
              </button>
              <button
                type="button"
                draggable={false}
                title={isEditingName ? 'Valider le nouveau nom' : 'Renommer ce repas'}
                aria-label={isEditingName ? `Valider le nom du repas ${meal.name || ''}`.trim() : `Renommer le repas ${meal.name || ''}`.trim()}
                className="meal-rename-btn-text"
              >
                <FontAwesomeIcon icon={isEditingName ? faCheck : faPen} />
                {isEditingName ? 'Valider' : 'Renommer'}
              </button>
              {isEditingName && (
                <button
                  type="button"
                  draggable={false}
                  title="Annuler la modification"
                  aria-label={`Annuler le renommage du repas ${meal.name || ''}`.trim()}
                  className="meal-cancel-btn-text"
                >
                  <FontAwesomeIcon icon={faXmark} />
                  Annuler
                </button>
              )}
              <button
                type="button"
                draggable={false}
                title="Supprimer ce repas"
                aria-label={`Supprimer le repas ${meal.name || ""}`.trim()}
                className="meal-delete-btn-text"
              >
                <FontAwesomeIcon icon={faTrash} />
                Supprimer
              </button>
            </div>
          )}
        </div>
        {isMobileViewport && (
          <div className={`meal-actions-panel ${isMealActionsOpen ? 'open' : ''}`}>
            <button
              type="button"
              draggable={false}
              title="Copier ce repas"
              aria-label={`Copier le repas ${meal.name || ''}`.trim()}
              className="meal-copy-btn-text"
            >
              <FontAwesomeIcon icon={faCopy} />
              Copier
            </button>
            <button
              type="button"
              draggable={false}
              title={isEditingName ? 'Valider le nouveau nom' : 'Renommer ce repas'}
              aria-label={isEditingName ? `Valider le nom du repas ${meal.name || ''}`.trim() : `Renommer le repas ${meal.name || ''}`.trim()}
              className="meal-rename-btn-text"
            >
              <FontAwesomeIcon icon={isEditingName ? faCheck : faPen} />
              {isEditingName ? 'Valider' : 'Renommer'}
            </button>
            {isEditingName && (
              <button
                type="button"
                draggable={false}
                title="Annuler la modification"
                aria-label={`Annuler le renommage du repas ${meal.name || ''}`.trim()}
                className="meal-cancel-btn-text"
              >
                <FontAwesomeIcon icon={faXmark} />
                Annuler
              </button>
            )}
            <button
              type="button"
              draggable={false}
              title="Supprimer ce repas"
              aria-label={`Supprimer le repas ${meal.name || ""}`.trim()}
              className="meal-delete-btn-text"
            >
              <FontAwesomeIcon icon={faTrash} />
              Supprimer
            </button>
          </div>
        )}

        <div className="meal-nutrition-summary">
          <div className="meal-summary-header">
            <div className="meal-summary-icon">
              <span className="summary-title">Total</span>
            </div>
            <div className="total-calories">
              <span className="calories-number">{mealCalories}</span>
              <span className="calories-unit">kcal</span>
            </div>
          </div>
          <div className="meal-total-macros-grid" aria-label="Macros totales du repas">
            <div className="meal-total-macro-chip proteins">
              <span className="meal-total-macro-label">Protéines</span>
              <span className="meal-total-macro-value">{formatGramCell(totals.proteins)} g</span>
            </div>
            <div className="meal-total-macro-chip carbs">
              <span className="meal-total-macro-label">Glucides</span>
              <span className="meal-total-macro-value">{formatGramCell(totals.carbohydrates)} g</span>
            </div>
            <div className="meal-total-macro-chip fat">
              <span className="meal-total-macro-label">Lipides</span>
              <span className="meal-total-macro-value">{formatGramCell(totals.fat)} g</span>
            </div>
          </div>
        </div>

        <div className="foods-list">
          {meal.foods.length === 0 ? (
            <div className="no-food-message">
              <p>Aucun aliment</p>
            </div>
          ) : (
            meal.foods.map((food, fIndex) => (
              <div
                className={`food-entry mobile-diary-food-entry ${draggedFood?.mealId === String(meal.id) && draggedFood?.foodIndex === fIndex ? 'dragging' : ''} ${foodOverTarget?.mealId === String(meal.id) && foodOverTarget?.foodIndex === fIndex ? 'over' : ''}`}
                key={food.id || fIndex}
                data-food-dnd-meal-id={String(meal.id)}
                data-food-dnd-index={fIndex}
                draggable={false}
              >
                {(() => {
                  const foodIngredientsKey = String(food.id || `${meal.id}-${fIndex}`);
                  const foodIngredients = Array.isArray(food.ingredients)
                    ? food.ingredients.filter((ingredient) => String(ingredient || '').trim().length > 0)
                    : [];
                  const detailedNutrition = calculateNutritionForDisplay(food.macronutrients, food.quantity);
                  const hasIngredients = foodIngredients.length > 0;
                  const isDetailsCollapsed = collapsedFoodIngredients[foodIngredientsKey] !== false;
                  const isMobileCompact = isMobileViewport;
                  const detailMacrosForFood = allDetailMacroKeys;
                  const secondaryMacrosForFood = detailMacrosForFood.filter(
                    (macroKey) => !foodPreviewMacros.includes(macroKey)
                  );
                  const hasDetailedMacros = hideUnavailableFoodMacros
                    ? detailMacrosForFood.some((macroKey) => {
                      const value = detailedNutrition[macroKey];
                      const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
                      return Number.isFinite(parsed);
                    })
                    : detailMacrosForFood.length > 0;
                  const hasDetails = hasDetailedMacros || hasIngredients || !isPremium;
                  const foodCalories = Math.round((calculateNutrition(food.macronutrients || {}, food.quantity || 100).energy) || 0);
                  const rawOffData = food?.openFoodFactsData
                    || food?.openfoodfactsData
                    || food?.openFoodFacts
                    || food?.offData
                    || food?.sourceData
                    || food?.source_data
                    || null;
                  const offData = parseFoodSourceData(rawOffData);
                  const foodDisplayName = food.name;
                  const nameKey = foodDisplayName.trim().toLowerCase();
                  const barcodeKey = getFoodBarcode(food, offData);
                  const cachedProduct = barcodeKey ? foodImageCache[barcodeKey] : null;
                  const cachedNameProduct = nameKey ? foodImageCache[`name:${nameKey}`] : null;
                  const rawFoodImageUrl = cachedProduct?.image_front_small_url
                    || cachedProduct?.image_thumb_url
                    || cachedProduct?.image_front_url
                    || cachedProduct?.image_url
                    || cachedNameProduct?.image_front_small_url
                    || cachedNameProduct?.image_thumb_url
                    || cachedNameProduct?.image_front_url
                    || cachedNameProduct?.image_url
                    || offData?.image_front_small_url
                    || offData?.image_thumb_url
                    || offData?.image_front_url
                    || offData?.image_url
                    || food?.imageUrl
                    || food?.image_url
                    || food?.image
                    || '';
                  const foodImageUrl = String(rawFoodImageUrl || '').trim();
                  const hasFoodImage = Boolean(foodImageUrl)
                    && foodImageUrl !== 'null'
                    && foodImageUrl !== 'undefined';
                  const fallbackInitial = String(foodDisplayName || '?').trim().charAt(0).toUpperCase();
                  const foodAddedTime = formatTimeShort(food.createdAt);
                  const showMobileDetails = isMobileCompact && !isDetailsCollapsed;
                  const hasPreviousMeal = index > 0 && !isPendingOfflineMeal(filteredMeals[index - 1]);
                  const hasNextMeal = index < filteredMeals.length - 1 && !isPendingOfflineMeal(filteredMeals[index + 1]);
                  const canMoveFoodUp = !mealIsPendingSync && (fIndex > 0 || hasPreviousMeal);
                  const canMoveFoodDown = !mealIsPendingSync && (fIndex < (meal.foods.length - 1) || hasNextMeal);
                  const favoritePayload = buildFavoriteFoodPayload({ food, displayName: foodDisplayName, sourceData: offData });
                  const isFavoriteFood = favoriteFoods.some((favorite) => matchesFavoriteFood(favorite, favoritePayload));

                  return (
                    <>
                      {isMobileViewport ? (
                        <div className="food-entry-header mobile-compact">
                          <div className="food-mobile-row">
                            <div className="food-mobile-main">
                              {hasFoodImage ? (
                                <button
                                  type="button"
                                  className="food-mobile-image-button"
                                  aria-label={`Agrandir l'image de ${foodDisplayName || 'cet aliment'}`}
                                >
                                  <img
                                    src={foodImageUrl}
                                    alt={foodDisplayName || 'Aliment'}
                                    className="food-mobile-image"
                                    loading="lazy"
                                    onError={(event) => {
                                      event.currentTarget.style.display = 'none';
                                    }}
                                  />
                                </button>
                              ) : (
                                <div className="food-mobile-image placeholder">
                                  {fallbackInitial}
                                </div>
                              )}
                              <div className="food-mobile-name-wrap">
                                <div className="food-mobile-name" title={foodDisplayName || ''}>
                                  {foodDisplayName}
                                </div>
                                <div className="food-mobile-quantity-inline" title="Quantité">
                                  {(food.quantity || 100)}g
                                  {foodAddedTime ? <span className="food-added-inline">• {foodAddedTime}</span> : null}
                                </div>
                              </div>
                            </div>
                            <div className="food-mobile-meta">
                              <div className="food-mobile-calories">
                                <span className="calories-amount">{foodCalories}</span>
                                <span className="calories-text">kcal</span>
                              </div>
                              {hasDetails && (
                                <button
                                  type="button"
                                  className={`food-ingredients-toggle compact ${isDetailsCollapsed ? '' : 'active'}`}
                                  aria-label={isDetailsCollapsed ? 'Afficher les détails de l’aliment' : 'Masquer les détails de l’aliment'}
                                >
                                  Détails
                                  <svg viewBox="0 0 24 24" width="14" height="14">
                                    <path
                                      d="M6.5 9.5 12 15l5.5-5.5"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2.8"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="food-mobile-actions">
                            <div className="food-mobile-sort-controls">
                              <button
                                type="button"
                                className="food-mobile-sort-btn"
                                disabled={!canMoveFoodUp}
                                aria-label={`Monter l'aliment ${foodDisplayName || ''}`.trim()}
                                title="Monter cet aliment"
                              >
                                <FontAwesomeIcon icon={faArrowUp} />
                              </button>
                              <button
                                type="button"
                                className="food-mobile-sort-btn"
                                disabled={!canMoveFoodDown}
                                aria-label={`Descendre l'aliment ${foodDisplayName || ''}`.trim()}
                                title="Descendre cet aliment"
                              >
                                <FontAwesomeIcon icon={faArrowDown} />
                              </button>
                            </div>
                            {!isReadOnly && (
                              <div className="food-mobile-main-actions">
                                <button
                                  type="button"
                                  className={`food-mobile-action-btn favorite ${isFavoriteFood ? 'active' : ''}`}
                                  disabled={mealIsPendingSync}
                                  title={isFavoriteFood ? `Retirer ${foodDisplayName || 'cet aliment'} des favoris` : `Ajouter ${foodDisplayName || 'cet aliment'} aux favoris`}
                                  aria-label={isFavoriteFood ? `Retirer ${foodDisplayName || 'cet aliment'} des favoris` : `Ajouter ${foodDisplayName || 'cet aliment'} aux favoris`}
                                >
                                  <FontAwesomeIcon icon={faStar} />
                                  {isFavoriteFood ? 'Retirer' : 'Ajouter'}
                                </button>
                                <button
                                  type="button"
                                  className="food-mobile-action-btn note"
                                  disabled={mealIsPendingSync}
                                  aria-label={`Modifier la note de ${foodDisplayName || 'cet aliment'}`}
                                  title="Ajouter ou modifier une note"
                                >
                                  <FontAwesomeIcon icon={faStickyNote} />
                                  Note
                                </button>
                                <button
                                  type="button"
                                  className="food-mobile-action-btn edit"
                                  disabled={mealIsPendingSync}
                                  aria-label={`Modifier ${foodDisplayName || 'cet aliment'}`}
                                  title="Modifier l'aliment"
                                >
                                  <FontAwesomeIcon icon={faPen} />
                                  Éditer
                                </button>
                                <button
                                  type="button"
                                  className="food-mobile-action-btn delete"
                                  disabled={mealIsPendingSync}
                                  aria-label={`Supprimer ${foodDisplayName || 'cet aliment'}`}
                                  title="Supprimer l'aliment"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                  Supprimer
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="food-entry-header">
                          <div className="food-info">
                            <h4 className="food-name">{foodDisplayName}</h4>
                            <div className="food-meta-row">
                              <div className="food-quantity" title="Quantité et heure d'ajout">
                                {food.quantity || 100}g
                                {foodAddedTime ? <span className="food-added-inline">• {foodAddedTime}</span> : null}
                              </div>
                            </div>
                            <div className="food-calories">
                              <span className="calories-amount">{foodCalories}</span>
                              <span className="calories-text">kcal</span>
                            </div>
                          </div>
                          {!isReadOnly && (
                            <div className="food-actions">
                              <button
                                type="button"
                                className="food-mobile-action-btn note"
                                title="Ajouter/modifier une note"
                              >
                                <FontAwesomeIcon icon={faStickyNote} />
                                Note
                              </button>
                              <button
                                type="button"
                                className="food-mobile-action-btn edit"
                                title="Modifier cet aliment"
                              >
                                <FontAwesomeIcon icon={faPen} />
                                Éditer
                              </button>
                              <button
                                type="button"
                                className="food-mobile-action-btn delete"
                                title="Supprimer cet aliment"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                                Supprimer
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {(!isMobileCompact || showMobileDetails) && food.note && food.note.trim() && (
                        <FoodNote>
                          <NoteIcon>
                            <FontAwesomeIcon icon={faStickyNote} />
                            Note
                          </NoteIcon>
                          <NoteText>{food.note}</NoteText>
                        </FoodNote>
                      )}

                      {!isMobileCompact && (
                        <FoodMacros
                          macronutrients={detailedNutrition}
                          mainMacros={foodPreviewMacros}
                          displayedMacros={[]}
                          showToggle={false}
                          hideUnavailableMacros={hideUnavailableFoodMacros}
                        />
                      )}

                      {hasDetails && !isMobileViewport && (
                        <button
                          type="button"
                          className={`food-ingredients-toggle ${isDetailsCollapsed ? '' : 'active'}`}
                          aria-label={isDetailsCollapsed ? 'Afficher les détails de l’aliment' : 'Masquer les détails de l’aliment'}
                        >
                          Détails
                          <svg viewBox="0 0 24 24" width="24" height="24">
                            <path
                              d="M6.5 9.5 12 15l5.5-5.5"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      )}
                      {!isDetailsCollapsed && (
                        <>
                          {hasDetailedMacros && (
                            <>
                              {isMobileCompact && (
                                <FoodMacros
                                  macronutrients={detailedNutrition}
                                  mainMacros={foodPreviewMacros}
                                  displayedMacros={[]}
                                  showToggle={false}
                                  hideUnavailableMacros={hideUnavailableFoodMacros}
                                />
                              )}
                              {secondaryMacrosForFood.length > 0 && (
                                <>
                                  {isMobileCompact && (
                                    <div className="food-macros-section-title">Macros secondaires</div>
                                  )}
                                  <FoodMacros
                                    macronutrients={detailedNutrition}
                                    mainMacros={[]}
                                    displayedMacros={secondaryMacrosForFood}
                                    renderMainMacros={false}
                                    expanded
                                    showToggle={false}
                                    hideUnavailableMacros={hideUnavailableFoodMacros}
                                    showSectionTitle={!isMobileCompact}
                                  />
                                </>
                              )}
                            </>
                          )}
                          {hasIngredients ? (
                            <div className="food-ingredients-block">
                              <div className="food-ingredients-title">Ingrédients</div>
                              <div className="food-ingredients">
                                <div className="food-ingredients-list">
                                  {foodIngredients.map((ingredient, ingredientIndex) => (
                                    <span
                                      key={`${foodIngredientsKey}-ingredient-${ingredientIndex}`}
                                      className="food-ingredient-chip"
                                    >
                                      {ingredient}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ) : null}
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
            ))
          )}
        </div>

        {!isReadOnly && (
          <div className="add-food-options mobile-diary-add-food-options">
            <button
              className="add-food-btn manual-btn"
              disabled={mealIsPendingSync}
              title={mealIsPendingSync ? 'Disponible après synchronisation du repas' : 'Ajouter un aliment'}
            >
              Ajouter
            </button>
            <button
              className="add-food-btn search-btn"
              disabled={mealIsPendingSync}
              title={mealIsPendingSync ? 'Disponible après synchronisation du repas' : 'Rechercher un aliment'}
            >
              Rechercher
            </button>
          </div>
        )}
      </div>
    );
  };

  const formatMobileCalories = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return '0';
    }
    return new Intl.NumberFormat('fr-FR').format(Math.round(numeric));
  };

  const renderMobileCalorieOverview = () => {
    const {
      hasConfiguredGoal,
      goalCalories,
      foodCalories,
      remainingCalories,
    } = mobileCalorieOverview;

    return (
      <section className="mobile-calorie-overview-card" aria-label="Calories restantes">
        <div className="mobile-calorie-overview-head">
          <h3>Calories restantes</h3>
          {!hasConfiguredGoal && (
            <span className="mobile-calorie-goal-hint">Objectif auto</span>
          )}
        </div>
        <div className="mobile-calorie-overview-math">
          <div className="mobile-calorie-metric">
            <span className="metric-value">{formatMobileCalories(goalCalories)}</span>
            <span className="metric-label">Objectif</span>
          </div>
          <span className="metric-operator" aria-hidden="true">-</span>
          <div className="mobile-calorie-metric">
            <span className="metric-value">{formatMobileCalories(foodCalories)}</span>
            <span className="metric-label">Consommé</span>
          </div>
          <span className="metric-operator" aria-hidden="true">=</span>
          <div className={`mobile-calorie-metric metric-result ${remainingCalories >= 0 ? 'positive' : 'negative'}`}>
            <span className="metric-value">{formatMobileCalories(remainingCalories)}</span>
            <span className="metric-label">Restant</span>
          </div>
        </div>
      </section>
    );
  };

  if (loading && !isInitialized) {
    return (
      <div className="journal-container">
        <div className="loading-state">
          <LoadingSpinner type="bars" size="medium" color="primary" showText={false} />
        </div>
      </div>
    );
  }

  if (error && !isInitialized) {
    return (
      <div className="journal-container">
        <div className="error-state">
          <div className="error-icon">
            <svg viewBox="0 0 24 24" width="32" height="32">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
          </div>
          <p>Erreur: {error}</p>
          <button onClick={clearError} className="btn btn-primary">
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // On ne montre que le premier repas pour la démo
  const demoMeals = filteredMeals.length > 0 ? [filteredMeals[0]] : [];

  return (
    <>
      <div className={`journal-container ${isReadOnly ? 'friend-view' : ''} ${demoData ? 'demo-mode' : ''}`}>

        <Toast />

        <FoodImageModal
          isOpen={Boolean(foodImagePreview)}
          images={
            foodImagePreview
              ? getFoodImageGallery(foodImagePreview)
              : []
          }
          fallbackSrc={PRODUCT_PLACEHOLDER_IMAGE}
          title={foodImagePreview?.name || "Produit"}
          subtitle={foodImagePreview?.brand || ""}
          code={foodImagePreview?.code || ""}
          onClose={() => setFoodImagePreview(null)}
        />

        <div className={`journal-container ${isMobileViewport ? 'mobile-journal-mode' : ''}`}>
          {/* 1. Navigation – texte au-dessus, contenu pleine largeur sans boîte */}
          <ScrollReveal>
          <StackedFeature>
            <FeatureText>
              <h3>
                <FontAwesomeIcon icon={faCalendarDays} /> Naviguez dans votre journal
              </h3>
              <p>Parcourez vos journées en un clic grâce aux flèches de navigation ou au sélecteur de date intégré.</p>
            </FeatureText>
            <div className="day-header">
              <h2 className="day-header-title">Journal Alimentaire</h2>
              <div className="day-header-nav">
                <button type="button" className="day-nav-btn" aria-label="Jour précédent">←</button>
                <SharedDatePicker />
                <button type="button" className="day-nav-btn" aria-label="Jour suivant">→</button>
              </div>
              {!isReadOnly && isMobileViewport && (
                <button
                  type="button"
                  className={`day-header-actions-toggle ${isMobileHeaderActionsOpen ? 'active' : ''}`}
                  aria-label={isMobileHeaderActionsOpen ? 'Masquer les actions du journal' : 'Afficher les actions du journal'}
                  aria-expanded={isMobileHeaderActionsOpen}
                >
                  Actions
                  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                    <path
                      d="M6.5 9.5 12 15l5.5-5.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
              {!isReadOnly && (
                <div className={`day-header-actions ${isMobileViewport ? (isMobileHeaderActionsOpen ? 'open' : 'collapsed') : ''}`}>
                  <button type="button" className="day-header-action-btn" disabled={isProcessing}>
                    <FontAwesomeIcon icon={faCopy} /> Copier
                  </button>
                  <button type="button" className="day-header-action-btn" disabled={!hasAnyExportableFoods}>
                    <FontAwesomeIcon icon={faFilePdf} /> Exporter PDF
                  </button>
                  <button type="button" className="day-header-action-btn" disabled={!hasAnyExportableFoods}>
                    <FontAwesomeIcon icon={faFileExcel} /> Exporter Excel
                  </button>
                </div>
              )}
            </div>
          </StackedFeature>
          </ScrollReveal>

          {/* 2. Calories restantes – inchangé (reste en deux colonnes avec boîte) */}
          <ScrollReveal>
          <TwoColRow $reverse>
            <FeatureText>
              <h3>
                <FontAwesomeIcon icon={faFire} /> Suivez votre budget calorique
              </h3>
              <p>Visualisez en un clin d’œil l’écart entre votre objectif, ce que vous avez déjà consommé et ce qu’il vous reste.</p>
            </FeatureText>
            {renderMobileCalorieOverview()}
          </TwoColRow>
          </ScrollReveal>

          {/* 3. Repas (un seul) – texte au-dessus, contenu pleine largeur sans boîte */}
          {demoMeals.map((meal, idx) => (
            <ScrollReveal key={meal.id}>
            <StackedFeature>
              <FeatureText>
                <h3>
                  <FontAwesomeIcon icon={faClock} /> Analysez chaque repas en profondeur
                </h3>
                <p>Chaque aliment est décomposé avec ses macronutriments. Le total du repas s’affiche en pied de tableau.</p>
              </FeatureText>
              {renderMealElement(meal, idx)}
            </StackedFeature>
            </ScrollReveal>
          ))}
          {demoMeals.length === 0 && (
            <ScrollReveal>
            <StackedFeature>
              <FeatureText>
                <h3>
                  <FontAwesomeIcon icon={faClock} /> Repas
                </h3>
                <p>Aucun repas à afficher pour cette journée.</p>
              </FeatureText>
              <div className="empty-state">
                <div className="empty-icon">
                  <svg viewBox="0 0 24 24" width="32" height="32">
                    <path d="M8.1 13.34l2.83-2.83L3.91 3.5c-1.56 1.56-1.56 4.09 0 5.66l4.19 4.18zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.2-1.1-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z" />
                  </svg>
                </div>
                <h3>Aucun repas</h3>
              </div>
            </StackedFeature>
            </ScrollReveal>
          )}

          {/* 4. Résumé de la journée – texte au-dessus, contenu pleine largeur sans boîte */}
          <ScrollReveal>
          <StackedFeature>
            <FeatureText>
              <h3>
                <FontAwesomeIcon icon={faClipboardList} /> Pilotez vos objectifs nutritionnels
              </h3>
              <p>Le résumé quotidien vous donne une vue synthétique de votre répartition en macronutriments.</p>
            </FeatureText>
            <SummaryDemo
              isPremium={isPremium}
              nutritionalGoals={nutritionalGoals}
              summaryMacros={summaryMacros}
              summaryFocusedMacros={SUMMARY_FOCUSED_MACROS}
              advancedGoalSummaryMacros={ADVANCED_GOAL_SUMMARY_MACROS}
              summaryMacroMapping={SUMMARY_MACRO_MAPPING}
              filteredMeals={filteredMeals}
              calculateNutrition={calculateNutrition}
            />
          </StackedFeature>
          </ScrollReveal>
        </div>
      </div>
    </>
  );
};

export default JournalDemo;
